import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  MessageSquare,
  BarChart3,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Gamepad2,
  Landmark,
  LogIn,
  LogOut,
  Package,
  Pencil,
  PlusCircle,
  Shield,
  ShoppingCart,
  Tag,
  UserPlus,
  Users,
  Trash2,
  Wrench,
  XCircle
} from 'lucide-react';

const API_BASE = '/api';
const TOKEN_STORAGE_KEY = 'finansam-auth-token';
const TENANT_VIEW_STORAGE_KEY = 'finansam-platform-tenant-view';
const MONTHLY_VIEW_STORAGE_KEY = 'finansam-monthly-view';
const PLATFORM_USERS_PAGE_SIZE = 10;
const TENANT_PLANS = ['Starter', 'Smart', 'Premium'];
const TENANT_LICENSE_TYPES = [
  { value: 'starter', label: 'Starter' },
  { value: 'smarter', label: 'Smarter' },
  { value: 'premium', label: 'Premium' }
];
const TENANT_STATUSES = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' }
];
const LICENSE_EXPIRED_MESSAGE = 'Sua licença expirou. Entre em contato para renovar.';
const WHATSAPP_SUPPORT_URL = `https://wa.me/5511993931524?text=${encodeURIComponent('Oi minha conta expirou consegue me ajudar')}`;

const defaultMonth = new Date().toISOString().slice(0, 7);
const defaultDate = new Date().toISOString().slice(0, 10);

const emptyCompra = () => ({ item: '', quantidade: '', valor: '', mes: defaultMonth });
const emptyConta = () => ({ tipo: '', valor: '', data: defaultDate });
const emptyEntrada = () => ({ tipo: '', valor: '', data: defaultDate, nota: '' });
const emptyLazer = () => ({ descricao: '', valor: '', data: defaultDate });
const emptyManutencao = () => ({ descricao: '', valor: '', data: defaultDate });
const emptyInvestimento = () => ({ descricao: '', valor: '', data: defaultDate, nota: '' });
const emptyNovoUsuario = () => ({ name: '', email: '', password: '' });
const emptyCreateTenantForm = () => ({
  firstName: '',
  lastName: '',
  company: '',
  plan: 'Starter',
  status: 'active',
  phone: '',
  email: '',
  createAdminUser: true,
  adminEmail: '',
  adminPassword: ''
});
const emptyEditTenantForm = () => ({
  firstName: '',
  lastName: '',
  company: '',
  plan: 'Starter',
  status: 'active',
  phone: '',
  email: '',
  createAdminUser: false,
  adminEmail: '',
  adminPassword: ''
});
const emptyPublicSignupForm = () => ({
  firstName: '',
  lastName: '',
  company: '',
  phone: '',
  email: '',
  adminEmail: '',
  adminPassword: ''
});
const defaultTenantSort = () => ({ key: 'name', direction: 'asc' });
const isMonthValueValid = (value) => /^\d{4}-\d{2}$/.test(String(value || ''));
const isDateValueValid = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));
const getRecordMonth = (record) => {
  const data = String(record?.data || '').slice(0, 10);

  if (isDateValueValid(data)) {
    return data.slice(0, 7);
  }

  const mes = String(record?.mes || '').slice(0, 7);
  return isMonthValueValid(mes) ? mes : null;
};
const getRecordDate = (record) => {
  const data = String(record?.data || '').slice(0, 10);

  if (isDateValueValid(data)) {
    return data;
  }

  const mes = getRecordMonth(record);
  return mes ? `${mes}-01` : defaultDate;
};
const formatDate = (value) => {
  const data = String(value || '').slice(0, 10);

  if (!isDateValueValid(data)) {
    return '-';
  }

  const [year, month, day] = data.split('-');
  return `${day}-${month}-${year}`;
};
const normalizeTenantSlug = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9-]+/g, '-')
  .replace(/-{2,}/g, '-')
  .replace(/^-+|-+$/g, '');

const normalizeTenantPlan = (value) => {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'starter') {
    return 'Starter';
  }

  if (normalized === 'smart') {
    return 'Smart';
  }

  if (normalized === 'smarter') {
    return 'Smart';
  }

  if (normalized === 'premium') {
    return 'Premium';
  }

  return null;
};

const normalizeTenantStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'active' || normalized === 'ativo') {
    return 'active';
  }

  if (normalized === 'inactive' || normalized === 'inativo') {
    return 'inactive';
  }

  if (normalized === 'trial' || normalized === 'teste') {
    return 'trial';
  }

  return null;
};

const getTrialDaysRemaining = (trialExpiresAt) => {
  if (!trialExpiresAt) {
    return null;
  }

  const expiresAt = new Date(trialExpiresAt).getTime();

  if (!Number.isFinite(expiresAt)) {
    return null;
  }

  const remainingMs = expiresAt - Date.now();
  return Math.max(0, Math.ceil(remainingMs / (24 * 60 * 60 * 1000)));
};

const licenseTypeFromTenant = (plan, status) => {
  const normalizedPlan = normalizeTenantPlan(plan);

  if (normalizedPlan === 'Starter') {
    return 'starter';
  }

  if (normalizedPlan === 'Smart') {
    return 'smarter';
  }

  if (normalizedPlan === 'Premium') {
    return 'premium';
  }

  return 'starter';
};

const tenantConfigFromLicenseType = (licenseType, currentStatus) => {
  const normalizedStatus = normalizeTenantStatus(currentStatus) || 'active';

  if (licenseType === 'starter') {
    return { plan: 'Starter', status: normalizedStatus === 'inactive' ? 'inactive' : 'active' };
  }

  if (licenseType === 'smarter') {
    return { plan: 'Smart', status: normalizedStatus === 'inactive' ? 'inactive' : 'active' };
  }

  if (licenseType === 'premium') {
    return { plan: 'Premium', status: normalizedStatus === 'inactive' ? 'inactive' : 'active' };
  }

  return { plan: 'Starter', status: normalizedStatus === 'inactive' ? 'inactive' : 'active' };
};

const formatTenantPlanLabel = (plan) => {
  const normalizedPlan = normalizeTenantPlan(plan);

  if (normalizedPlan === 'Smart') {
    return 'Smarter';
  }

  return normalizedPlan || '-';
};

const readTenantViewPreferences = () => {
  try {
    const raw = localStorage.getItem(TENANT_VIEW_STORAGE_KEY);

    if (!raw) {
      return {
        planFilter: 'all',
        statusFilter: 'all',
        searchTerm: '',
        sortConfig: defaultTenantSort()
      };
    }

    const parsed = JSON.parse(raw);
    const allowedKeys = ['name', 'plan', 'subscription_status'];
    const nextKey = allowedKeys.includes(parsed?.sortConfig?.key) ? parsed.sortConfig.key : 'name';
    const nextDirection = parsed?.sortConfig?.direction === 'desc' ? 'desc' : 'asc';
    const nextPlanFilter = typeof parsed?.planFilter === 'string' ? parsed.planFilter : 'all';
    const isPlanFilterValid = nextPlanFilter === 'all' || TENANT_PLANS.includes(nextPlanFilter);

    return {
      planFilter: isPlanFilterValid ? nextPlanFilter : 'all',
      statusFilter: typeof parsed?.statusFilter === 'string' ? parsed.statusFilter : 'all',
      searchTerm: typeof parsed?.searchTerm === 'string' ? parsed.searchTerm : '',
      sortConfig: { key: nextKey, direction: nextDirection }
    };
  } catch (_error) {
    return {
      planFilter: 'all',
      statusFilter: 'all',
      searchTerm: '',
      sortConfig: defaultTenantSort()
    };
  }
};

const readMonthlyViewPreferences = () => {
  try {
    const raw = localStorage.getItem(MONTHLY_VIEW_STORAGE_KEY);

    if (!raw) {
      return {
        reportMonth: defaultMonth,
        comprasMonth: defaultMonth,
        contasMonth: defaultMonth,
        entradasMonth: defaultMonth,
        lazerMonth: defaultMonth,
        manutencoesMonth: defaultMonth,
        investimentosMonth: defaultMonth
      };
    }

    const parsed = JSON.parse(raw);

    return {
      reportMonth: isMonthValueValid(parsed?.reportMonth) ? parsed.reportMonth : defaultMonth,
      comprasMonth: isMonthValueValid(parsed?.comprasMonth) ? parsed.comprasMonth : defaultMonth,
      contasMonth: isMonthValueValid(parsed?.contasMonth) ? parsed.contasMonth : defaultMonth,
      entradasMonth: isMonthValueValid(parsed?.entradasMonth) ? parsed.entradasMonth : defaultMonth,
      lazerMonth: isMonthValueValid(parsed?.lazerMonth) ? parsed.lazerMonth : defaultMonth,
      manutencoesMonth: isMonthValueValid(parsed?.manutencoesMonth) ? parsed.manutencoesMonth : defaultMonth,
      investimentosMonth: isMonthValueValid(parsed?.investimentosMonth) ? parsed.investimentosMonth : defaultMonth
    };
  } catch (_error) {
    return {
      reportMonth: defaultMonth,
      comprasMonth: defaultMonth,
      contasMonth: defaultMonth,
      entradasMonth: defaultMonth,
      lazerMonth: defaultMonth,
      manutencoesMonth: defaultMonth,
      investimentosMonth: defaultMonth
    };
  }
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
});

const tabs = [
  { key: 'compras', label: 'Compras do Mês', icon: ShoppingCart },
  { key: 'contas', label: 'Contas Fixas', icon: DollarSign },
  { key: 'entradas', label: 'Entradas', icon: PlusCircle },
  { key: 'lazer', label: 'Lazer', icon: Gamepad2 },
  { key: 'investimentos', label: 'Investimentos', icon: Landmark },
  { key: 'manutencoes', label: 'Extraordinárias', icon: Wrench },
  { key: 'estoque', label: 'Estoque', icon: Package },
  { key: 'relatorios', label: 'Relatórios', icon: BarChart3 }
];

const adminTabs = [
  { key: 'admin', label: 'Portal Admin', icon: Shield },
  { key: 'categories', label: 'Categorias', icon: Tag }
];
const platformTabs = [{ key: 'platform', label: 'Plataforma', icon: Building2 }];

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);

const normalizeError = (error) => {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Não foi possível concluir a operação';
};

const valueToPercent = (value, total) => (Number(value) / Number(total)) * 100;

const SectionHeader = ({ title, description }) => (
  <div className="rounded-[24px] border border-slate-200/80 bg-[linear-gradient(135deg,rgba(255,255,255,0.92)_0%,rgba(248,250,252,0.98)_100%)] px-5 py-5 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
    <h2 className="text-3xl font-black tracking-tight text-slate-900">{title}</h2>
    <p className="mt-2 text-slate-600">{description}</p>
  </div>
);

const Field = ({ type = 'text', value, onChange, placeholder }) => (
  <input
    type={type}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
  />
);

const PrimaryButton = ({ icon: Icon, onClick, label, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
  >
    <Icon size={18} /> {label}
  </button>
);

const ActionButton = ({ icon: Icon, onClick, label, tone = 'primary', disabled }) => {
  const base = 'inline-flex items-center justify-center gap-2 rounded-xl text-sm font-semibold transition';
  const tones = {
    primary: `${base} bg-emerald-600 text-white px-3 py-1 hover:bg-emerald-500`,
    secondary: `${base} bg-slate-100 text-slate-800 px-3 py-1 hover:bg-slate-200`,
    danger: `${base} bg-rose-100 text-rose-700 px-3 py-1 hover:bg-rose-200`
  };

  return (
    <button type="button" onClick={onClick} disabled={disabled} className={tones[tone]}>
      {Icon && <Icon size={14} />}
      <span>{label}</span>
    </button>
  );
};

const DangerTextButton = ({ onClick, label }) => (
  <button type="button" onClick={onClick} className="text-sm font-semibold text-rose-600 transition hover:text-rose-700">
    {label}
  </button>
);

const SecondaryTextButton = ({ onClick, label }) => (
  <button type="button" onClick={onClick} className="text-sm font-semibold text-slate-700 transition hover:text-slate-900">
    {label}
  </button>
);

const getPreviousMonth = (monthStr) => {
  const [year, month] = monthStr.split('-').map(Number);
  if (month === 1) {
    return `${year - 1}-12`;
  }
  return `${year}-${String(month - 1).padStart(2, '0')}`;
};

const getNextMonth = (monthStr) => {
  const [year, month] = monthStr.split('-').map(Number);
  if (month === 12) {
    return `${year + 1}-01`;
  }
  return `${year}-${String(month + 1).padStart(2, '0')}`;
};

const monthShortLabels = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const monthLongLabels = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const parseMonthValue = (monthStr) => {
  const fallbackYear = Number(defaultMonth.slice(0, 4));
  const fallbackMonth = Number(defaultMonth.slice(5, 7));

  if (!/^\d{4}-\d{2}$/.test(String(monthStr || ''))) {
    return { year: fallbackYear, month: fallbackMonth };
  }

  const [year, month] = monthStr.split('-').map(Number);
  return { year, month };
};

const formatMonthLongLabel = (monthStr) => {
  const { year, month } = parseMonthValue(monthStr);
  return `${monthLongLabels[month - 1]} de ${year}`;
};

const MonthPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef(null);
  const { year: selectedYear, month: selectedMonth } = parseMonthValue(value);
  const [displayYear, setDisplayYear] = useState(selectedYear);

  useEffect(() => {
    setDisplayYear(selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!pickerRef.current || pickerRef.current.contains(event.target)) {
        return;
      }

      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={pickerRef} className="relative flex-1">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-slate-800 shadow-[0_8px_20px_rgba(15,23,42,0.06)] transition hover:border-slate-300"
      >
        <span className="font-medium capitalize">{formatMonthLongLabel(value)}</span>
        <CalendarDays size={18} className="text-slate-500" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-2 w-full min-w-[280px] rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_20px_45px_rgba(15,23,42,0.18)]">
          <div className="mb-3 flex items-center justify-between rounded-xl bg-slate-50 px-2 py-2">
            <button
              type="button"
              onClick={() => setDisplayYear((current) => current - 1)}
              className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-200"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-bold text-slate-800">{displayYear}</span>
            <button
              type="button"
              onClick={() => setDisplayYear((current) => current + 1)}
              className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-200"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {monthShortLabels.map((label, monthIndex) => {
              const monthNumber = monthIndex + 1;
              const monthValue = `${displayYear}-${String(monthNumber).padStart(2, '0')}`;
              const isActive = displayYear === selectedYear && monthNumber === selectedMonth;

              return (
                <button
                  key={monthValue}
                  type="button"
                  onClick={() => {
                    onChange(monthValue);
                    setOpen(false);
                  }}
                  className={`rounded-lg px-2 py-2 text-sm font-semibold uppercase transition ${isActive ? 'bg-slate-900 text-white shadow-[0_8px_20px_rgba(15,23,42,0.25)]' : 'bg-slate-50 text-slate-700 hover:bg-slate-100'}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => {
                onChange(defaultMonth);
                setOpen(false);
              }}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Ir para mês atual
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const FilterMonth = ({ value, onChange }) => (
  <div className="mt-6 w-full">
    <label className="mb-2 block text-sm font-semibold text-slate-700">Filtrar por mês</label>
    <div className="flex w-full gap-2">
      <button
        type="button"
        onClick={() => onChange(getPreviousMonth(value))}
        className="shrink-0 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
      >
        ← Anterior
      </button>
      <MonthPicker value={value} onChange={onChange} />
      <button
        type="button"
        onClick={() => onChange(getNextMonth(value))}
        className="shrink-0 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
      >
        Próximo →
      </button>
    </div>
  </div>
);

const MetricCard = ({ title, value, tone }) => (
  <div className={`rounded-[28px] border border-white/50 p-5 shadow-[0_14px_35px_rgba(15,23,42,0.08)] ${tone}`}>
    <p className="text-sm font-semibold uppercase tracking-[0.18em]">{title}</p>
    <p className="mt-3 text-3xl font-black">{value}</p>
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-3 last:border-b-0 last:pb-0">
    <span className="text-sm text-slate-300">{label}</span>
    <span className="text-lg font-bold text-white">{value}</span>
  </div>
);

const DataTable = ({ headers, rows, emptyMessage }) => (
  <div className="mt-6 overflow-x-auto rounded-[28px] border border-slate-200/80 bg-white/95 shadow-[0_16px_40px_rgba(15,23,42,0.06)]">
    <table className="min-w-full bg-transparent text-left text-sm">
      <thead className="bg-[linear-gradient(135deg,#f8fafc_0%,#eef2f7_100%)] text-slate-600">
        <tr>
          {headers.map((header) => (
            <th key={header} className="px-4 py-4 font-semibold uppercase tracking-[0.08em] text-slate-500">{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`row-${index}`} className="border-t border-slate-100/90 text-slate-700 transition hover:bg-slate-50/80">
            {row.map((cell, cellIndex) => (
              <td key={`cell-${index}-${cellIndex}`} className="px-4 py-4 align-top">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-500">{emptyMessage}</p>}
  </div>
);

const ErrorMessageText = ({ message }) => {
  if (String(message || '').toLowerCase().includes('sua licença expirou')) {
    return (
      <>
        {LICENSE_EXPIRED_MESSAGE}{' '}
        <a
          href={WHATSAPP_SUPPORT_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-3 py-1 text-sm font-semibold text-white transition hover:bg-emerald-500"
        >
          <MessageSquare size={16} />
          <span>WhatsApp</span>
        </a>
      </>
    );
  }

  return <>{message}</>;
};

const FinanceApp = () => {
  const tenantViewPreferences = readTenantViewPreferences();
  const monthlyViewPreferences = readMonthlyViewPreferences();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY) || '');
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('compras');
  const [compras, setCompras] = useState([]);
  const [contas, setContas] = useState([]);
  const [entradas, setEntradas] = useState([]);
  const [lazer, setLazer] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [investimentos, setInvestimentos] = useState([]);
  const [manutencoes, setManutencoes] = useState([]);
  const [estoque, setEstoque] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [platformTenants, setPlatformTenants] = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [platformAdmins, setPlatformAdmins] = useState([]);
  const [platformUsersSearchTerm, setPlatformUsersSearchTerm] = useState('');
  const [platformUsersRoleFilter, setPlatformUsersRoleFilter] = useState('all');
  const [platformUsersStatusFilter, setPlatformUsersStatusFilter] = useState('all');
  const [platformUsersPage, setPlatformUsersPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(Boolean(token));
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [novaCompra, setNovaCompra] = useState(emptyCompra());
  const [novaConta, setNovaConta] = useState(emptyConta());
  const [novaEntrada, setNovaEntrada] = useState(emptyEntrada());
  const [novoLazer, setNovoLazer] = useState(emptyLazer());
  const [novoInvestimento, setNovoInvestimento] = useState(emptyInvestimento());
  const [novaManutencao, setNovaManutencao] = useState(emptyManutencao());
  const [editingCompraId, setEditingCompraId] = useState(null);
  const [editingContaId, setEditingContaId] = useState(null);
  const [editingEntradaId, setEditingEntradaId] = useState(null);
  const [editingLazerId, setEditingLazerId] = useState(null);
  const [editingManutencaoId, setEditingManutencaoId] = useState(null);
  const [editingInvestimentoId, setEditingInvestimentoId] = useState(null);
  const [novoUsuario, setNovoUsuario] = useState(emptyNovoUsuario());
  const [novaCategoriaConta, setNovaCategoriaConta] = useState('');
  const [novaCategoriaEntrada, setNovaCategoriaEntrada] = useState('');
  const [novaCategoriaInvestimento, setNovaCategoriaInvestimento] = useState('');
  const [showCreateTenantForm, setShowCreateTenantForm] = useState(false);
  const [createTenantForm, setCreateTenantForm] = useState(emptyCreateTenantForm());
  const [showPublicSignupForm, setShowPublicSignupForm] = useState(false);
  const [publicSignupForm, setPublicSignupForm] = useState(emptyPublicSignupForm());
  const [editTenantForm, setEditTenantForm] = useState(emptyEditTenantForm());
  const [editingTenantId, setEditingTenantId] = useState(null);
  const [tenantPlanFilter, setTenantPlanFilter] = useState(tenantViewPreferences.planFilter);
  const [tenantStatusFilter, setTenantStatusFilter] = useState(tenantViewPreferences.statusFilter);
  const [tenantSearchTerm, setTenantSearchTerm] = useState(tenantViewPreferences.searchTerm);
  const [tenantSortConfig, setTenantSortConfig] = useState(tenantViewPreferences.sortConfig);
  const [senhaResetUsuario, setSenhaResetUsuario] = useState({});
  const [mesRelatorio, setMesRelatorio] = useState(monthlyViewPreferences.reportMonth);
  const [mesFiltroCompras, setMesFiltroCompras] = useState(monthlyViewPreferences.comprasMonth);
  const [mesFiltroContas, setMesFiltroContas] = useState(monthlyViewPreferences.contasMonth);
  const [mesFiltroEntradas, setMesFiltroEntradas] = useState(monthlyViewPreferences.entradasMonth);
  const [mesFiltroLazer, setMesFiltroLazer] = useState(monthlyViewPreferences.lazerMonth);
  const [mesFiltroManutencoes, setMesFiltroManutencoes] = useState(monthlyViewPreferences.manutencoesMonth);
  const [mesFiltroInvestimentos, setMesFiltroInvestimentos] = useState(monthlyViewPreferences.investimentosMonth);
  const [baixasEstoque, setBaixasEstoque] = useState({});

  const isPlatformAdmin = user?.scope === 'platform' || user?.role === 'super_admin';
  const isAdmin = !isPlatformAdmin && (user?.role === 'admin' || user?.role === 'owner');
  const tenantPlan = normalizeTenantPlan(user?.tenantPlan || user?.tenant_plan || '');
  const tenantSubscriptionStatus = normalizeTenantStatus(user?.tenantSubscriptionStatus || user?.subscription_status || '');
  const trialDaysRemaining = getTrialDaysRemaining(user?.tenantTrialExpiresAt || user?.trial_expires_at);
  const showLicenseCountdown = !isPlatformAdmin
    && tenantPlan !== null
    && trialDaysRemaining !== null;

  const apiFetch = async (path, options = {}) => {
    const authToken = options.authToken ?? token;
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers
    });

    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await response.json() : null;

    if (!response.ok) {
      const message = data?.error || data?.message || 'Falha na requisição';

      if (response.status === 401) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setToken('');
        setUser(null);
      }

      const err = new Error(message);
      // attach HTTP status so callers can decide how to handle it
      err.status = response.status;
      throw err;
    }

    return data;
  };

  const clearMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const loadProtectedData = async (sessionUser = user, authToken) => {
    const isPlatformScope = sessionUser?.scope === 'platform' || sessionUser?.role === 'super_admin';

    if (isPlatformScope) {
      const [tenantsData, usersData, adminsData] = await Promise.all([
        apiFetch('/platform/tenants', { authToken }),
        apiFetch('/platform/users', { authToken }),
        apiFetch('/platform/admins', { authToken })
      ]);

      setPlatformTenants(tenantsData);
      setPlatformUsers(usersData);
      setPlatformAdmins(adminsData);
      setCompras([]);
      setContas([]);
      setEntradas([]);
      setLazer([]);
      setCategorias([]);
      setInvestimentos([]);
      setManutencoes([]);
      setEstoque([]);
      setUsuarios([]);
      return;
    }

    const shouldLoadUsers = sessionUser?.role === 'admin' || sessionUser?.role === 'owner';
    const requests = [
      apiFetch('/compras', { authToken }),
      apiFetch('/contas', { authToken }),
      apiFetch('/entradas', { authToken }),
      apiFetch('/lazer', { authToken }),
      apiFetch('/categories', { authToken }),
      apiFetch('/investimentos', { authToken }),
      apiFetch('/manutencoes', { authToken }),
      apiFetch('/estoque', { authToken })
    ];

    if (shouldLoadUsers) {
      requests.push(apiFetch('/users', { authToken }));
    }

    const [comprasData, contasData, entradasData, lazerData, categoriasData, investimentosData, manutencoesData, estoqueData, usersData] = await Promise.all(requests);

    setCompras(comprasData);
    setContas(contasData);
    setEntradas(entradasData);
    setLazer(lazerData);
    setCategorias(categoriasData);
    setInvestimentos(investimentosData);
    setManutencoes(manutencoesData);
    setEstoque(estoqueData);

    if (shouldLoadUsers) {
      setUsuarios(usersData);
    } else {
      setUsuarios([]);
    }

    setPlatformTenants([]);
    setPlatformUsers([]);
    setPlatformAdmins([]);
  };

  useEffect(() => {
    const bootstrap = async () => {
      if (!token) {
        setSessionLoading(false);
        return;
      }

      try {
        const session = await apiFetch('/auth/me');
        setUser(session.user);
        setActiveTab(session.user.scope === 'platform' ? 'platform' : 'compras');
        await loadProtectedData(session.user, token);
      } catch (error) {
        // If session ended / unauthorized, do not show an error message to the user
        if (error?.status === 401) {
          setErrorMessage('');
        } else {
          setErrorMessage(normalizeError(error));
        }
      } finally {
        setSessionLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    localStorage.setItem(
      TENANT_VIEW_STORAGE_KEY,
      JSON.stringify({
        planFilter: tenantPlanFilter,
        statusFilter: tenantStatusFilter,
        searchTerm: tenantSearchTerm,
        sortConfig: tenantSortConfig
      })
    );
  }, [tenantPlanFilter, tenantStatusFilter, tenantSearchTerm, tenantSortConfig]);

  useEffect(() => {
    localStorage.setItem(
      MONTHLY_VIEW_STORAGE_KEY,
      JSON.stringify({
        reportMonth: mesRelatorio,
        comprasMonth: mesFiltroCompras,
        contasMonth: mesFiltroContas,
        entradasMonth: mesFiltroEntradas,
        lazerMonth: mesFiltroLazer,
        manutencoesMonth: mesFiltroManutencoes,
        investimentosMonth: mesFiltroInvestimentos
      })
    );
  }, [mesRelatorio, mesFiltroCompras, mesFiltroContas, mesFiltroEntradas, mesFiltroLazer, mesFiltroManutencoes, mesFiltroInvestimentos]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken('');
    setUser(null);
    setCompras([]);
    setContas([]);
    setEntradas([]);
    setLazer([]);
    setCategorias([]);
    setInvestimentos([]);
    setManutencoes([]);
    setEstoque([]);
    setUsuarios([]);
    setPlatformTenants([]);
    setPlatformUsers([]);
    setPlatformAdmins([]);
    setPlatformUsersSearchTerm('');
    setPlatformUsersRoleFilter('all');
    setPlatformUsersStatusFilter('all');
    setPlatformUsersPage(0);
    clearMessages();
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    clearMessages();
    setLoading(true);

    try {
      const data = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify(loginForm)
      });

      localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
      setActiveTab(data.user.scope === 'platform' ? 'platform' : 'compras');
      await loadProtectedData(data.user, data.token);
      setLoginForm({ email: '', password: '' });
      setSuccessMessage(`Sessão iniciada como ${data.user.name}`);
    } catch (error) {
      setErrorMessage(normalizeError(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePublicSignup = async (event) => {
    event.preventDefault();
    clearMessages();
    setLoading(true);

    try {
      const data = await apiFetch('/auth/signup', {
        method: 'POST',
        authToken: '',
        body: JSON.stringify(publicSignupForm)
      });

      localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
      setActiveTab('compras');
      await loadProtectedData(data.user, data.token);
      setPublicSignupForm(emptyPublicSignupForm());
      setShowPublicSignupForm(false);
      setSuccessMessage('Cadastro criado com licença Starter de 7 dias');
    } catch (error) {
      setErrorMessage(normalizeError(error));
    } finally {
      setLoading(false);
    }
  };

  const runMutation = async (action, successText) => {
    clearMessages();
    setLoading(true);

    try {
      await action();
      await loadProtectedData();
      setSuccessMessage(successText);
    } catch (error) {
      setErrorMessage(normalizeError(error));
    } finally {
      setLoading(false);
    }
  };

  const adicionarCompra = async () => {
    if (!novaCompra.item || !novaCompra.quantidade || !novaCompra.valor || !novaCompra.mes) {
      setErrorMessage('Preencha todos os campos da compra');
      return;
    }

    await runMutation(async () => {
      if (editingCompraId) {
        await apiFetch(`/compras/${editingCompraId}`, {
          method: 'PATCH',
          body: JSON.stringify(novaCompra)
        });
        setEditingCompraId(null);
      } else {
        await apiFetch('/compras', {
          method: 'POST',
          body: JSON.stringify(novaCompra)
        });
      }
      setNovaCompra(emptyCompra());
    }, editingCompraId ? 'Compra atualizada com sucesso' : 'Compra adicionada com sucesso');
  };

  const adicionarConta = async () => {
    if (!novaConta.valor || !novaConta.data || !novaConta.tipo) {
      setErrorMessage('Preencha todos os campos da conta');
      return;
    }

    await runMutation(async () => {
      if (editingContaId) {
        await apiFetch(`/contas/${editingContaId}`, {
          method: 'PATCH',
          body: JSON.stringify(novaConta)
        });
        setEditingContaId(null);
      } else {
        await apiFetch('/contas', {
          method: 'POST',
          body: JSON.stringify(novaConta)
        });
      }
      setNovaConta(emptyConta());
    }, editingContaId ? 'Conta atualizada com sucesso' : 'Conta adicionada com sucesso');
  };

  const adicionarLazer = async () => {
    if (!novoLazer.descricao || !novoLazer.valor || !novoLazer.data) {
      setErrorMessage('Preencha todos os campos de lazer');
      return;
    }

    await runMutation(async () => {
      if (editingLazerId) {
        await apiFetch(`/lazer/${editingLazerId}`, {
          method: 'PATCH',
          body: JSON.stringify(novoLazer)
        });
        setEditingLazerId(null);
      } else {
        await apiFetch('/lazer', {
          method: 'POST',
          body: JSON.stringify(novoLazer)
        });
      }
      setNovoLazer(emptyLazer());
    }, editingLazerId ? 'Despesa de lazer atualizada com sucesso' : 'Despesa de lazer adicionada com sucesso');
  };

  const confirmarLazer = async (id) => {
    clearMessages();
    try {
      await runMutation(async () => {
        await apiFetch(`/lazer/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'executed' })
        });
      }, 'Despesa marcada como efetivada');
      await loadProtectedData(user, token);
    } catch (error) {
      setErrorMessage(normalizeError(error));
    }
  };

  const confirmarEntrada = async (id) => {
    clearMessages();
    try {
      await runMutation(async () => {
        await apiFetch(`/entradas/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'executed' })
        });
      }, 'Entrada marcada como efetivada');
      await loadProtectedData(user, token);
    } catch (error) {
      setErrorMessage(normalizeError(error));
    }
  };

  const confirmarInvestimento = async (id) => {
    clearMessages();
    try {
      await runMutation(async () => {
        await apiFetch(`/investimentos/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'executed' })
        });
      }, 'Investimento marcado como efetivado');
      await loadProtectedData(user, token);
    } catch (error) {
      setErrorMessage(normalizeError(error));
    }
  };

  const confirmarManutencao = async (id) => {
    clearMessages();
    try {
      await runMutation(async () => {
        await apiFetch(`/manutencoes/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'executed' })
        });
      }, 'Despesa extraordinária marcada como efetivada');
      await loadProtectedData(user, token);
    } catch (error) {
      setErrorMessage(normalizeError(error));
    }
  };

  const confirmarConta = async (id) => {
    clearMessages();
    try {
      await runMutation(async () => {
        await apiFetch(`/contas/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'executed' })
        });
      }, 'Conta marcada como efetivada');
      await loadProtectedData(user, token);
    } catch (error) {
      setErrorMessage(normalizeError(error));
    }
  };

  const adicionarEntrada = async () => {
    if (!novaEntrada.valor || !novaEntrada.data || !novaEntrada.tipo) {
      setErrorMessage('Preencha todos os campos da entrada');
      return;
    }

    await runMutation(async () => {
      if (editingEntradaId) {
        await apiFetch(`/entradas/${editingEntradaId}`, {
          method: 'PATCH',
          body: JSON.stringify(novaEntrada)
        });
        setEditingEntradaId(null);
      } else {
        await apiFetch('/entradas', {
          method: 'POST',
          body: JSON.stringify(novaEntrada)
        });
      }
      setNovaEntrada(emptyEntrada());
    }, editingEntradaId ? 'Entrada atualizada com sucesso' : 'Entrada adicionada com sucesso');
  };

  const adicionarManutencao = async () => {
    if (!novaManutencao.descricao || !novaManutencao.valor || !novaManutencao.data) {
      setErrorMessage('Preencha todos os campos da manutenção');
      return;
    }

    await runMutation(async () => {
      if (editingManutencaoId) {
        await apiFetch(`/manutencoes/${editingManutencaoId}`, {
          method: 'PATCH',
          body: JSON.stringify(novaManutencao)
        });
        setEditingManutencaoId(null);
      } else {
        await apiFetch('/manutencoes', {
          method: 'POST',
          body: JSON.stringify(novaManutencao)
        });
      }
      setNovaManutencao(emptyManutencao());
    }, editingManutencaoId ? 'Despesa extraordinária atualizada com sucesso' : 'Despesa extraordinária adicionada com sucesso');
  };

  const adicionarInvestimento = async () => {
    if (!novoInvestimento.descricao || !novoInvestimento.valor || !novoInvestimento.data) {
      setErrorMessage('Preencha todos os campos do investimento');
      return;
    }

    await runMutation(async () => {
      if (editingInvestimentoId) {
        await apiFetch(`/investimentos/${editingInvestimentoId}`, {
          method: 'PATCH',
          body: JSON.stringify(novoInvestimento)
        });
        setEditingInvestimentoId(null);
      } else {
        await apiFetch('/investimentos', {
          method: 'POST',
          body: JSON.stringify(novoInvestimento)
        });
      }
      setNovoInvestimento(emptyInvestimento());
    }, editingInvestimentoId ? 'Investimento atualizado com sucesso' : 'Investimento adicionado com sucesso');
  };

  const iniciarEdicaoCompra = (compra) => {
    setActiveTab('compras');
    setEditingCompraId(compra.id);
    setNovaCompra({ item: compra.item || '', quantidade: String(compra.quantidade ?? ''), valor: String(compra.valor ?? ''), mes: compra.mes || defaultMonth });
    clearMessages();
  };

  const iniciarEdicaoConta = (conta) => {
    setActiveTab('contas');
    setEditingContaId(conta.id);
    setNovaConta({ tipo: conta.tipo || '', valor: String(conta.valor ?? ''), data: getRecordDate(conta) });
    clearMessages();
  };

  const iniciarEdicaoEntrada = (entrada) => {
    setActiveTab('entradas');
    setEditingEntradaId(entrada.id);
    setNovaEntrada({
      tipo: entrada.tipo || '',
      valor: String(entrada.valor ?? ''),
      data: getRecordDate(entrada),
      nota: entrada.nota || ''
    });
    clearMessages();
  };

  const iniciarEdicaoLazer = (item) => {
    setActiveTab('lazer');
    setEditingLazerId(item.id);
    setNovoLazer({ descricao: item.descricao || '', valor: String(item.valor ?? ''), data: getRecordDate(item) });
    clearMessages();
  };

  const iniciarEdicaoManutencao = (manutencao) => {
    setActiveTab('manutencoes');
    setEditingManutencaoId(manutencao.id);
    setNovaManutencao({
      descricao: manutencao.descricao || '',
      valor: String(manutencao.valor ?? ''),
      data: String(manutencao.data || '').slice(0, 10) || defaultDate
    });
    clearMessages();
  };

  const iniciarEdicaoInvestimento = (investimento) => {
    setActiveTab('investimentos');
    setEditingInvestimentoId(investimento.id);
    setNovoInvestimento({
      descricao: investimento.descricao || '',
      valor: String(investimento.valor ?? ''),
      data: getRecordDate(investimento),
      nota: investimento.nota || ''
    });
    clearMessages();
  };

  const cancelarEdicaoCompra = () => {
    setEditingCompraId(null);
    setNovaCompra(emptyCompra());
  };

  const cancelarEdicaoConta = () => {
    setEditingContaId(null);
    setNovaConta(emptyConta());
  };

  const cancelarEdicaoEntrada = () => {
    setEditingEntradaId(null);
    setNovaEntrada(emptyEntrada());
  };

  const cancelarEdicaoLazer = () => {
    setEditingLazerId(null);
    setNovoLazer(emptyLazer());
  };

  const cancelarEdicaoManutencao = () => {
    setEditingManutencaoId(null);
    setNovaManutencao(emptyManutencao());
  };

  const cancelarEdicaoInvestimento = () => {
    setEditingInvestimentoId(null);
    setNovoInvestimento(emptyInvestimento());
  };

  const adicionarCategoria = async (scope, rawName) => {
    const name = rawName.trim();

    if (!name) {
      setErrorMessage('Informe o nome da categoria');
      return;
    }

    await runMutation(async () => {
      await apiFetch('/categories', {
        method: 'POST',
        body: JSON.stringify({ scope, name })
      });

      if (scope === 'contas') {
        setNovaCategoriaConta('');
      }

      if (scope === 'entradas') {
        setNovaCategoriaEntrada('');
      }

      if (scope === 'investimentos') {
        setNovaCategoriaInvestimento('');
      }
    }, 'Categoria adicionada com sucesso');
  };

  const darBaixaEstoque = async (id) => {
    const quantidade = Number(baixasEstoque[id]);

    if (!Number.isFinite(quantidade) || quantidade <= 0) {
      setErrorMessage('Informe uma quantidade válida para baixa');
      return;
    }

    await runMutation(async () => {
      await apiFetch(`/estoque/${id}/baixa`, {
        method: 'PATCH',
        body: JSON.stringify({ quantidade })
      });
      setBaixasEstoque((current) => ({ ...current, [id]: '' }));
    }, 'Baixa de estoque realizada com sucesso');
  };

  const excluirRegistro = async (path, successText) => {
    await runMutation(async () => {
      await apiFetch(path, { method: 'DELETE' });
    }, successText);
  };

  const excluirCategoria = async (categoryId) => {
    await runMutation(async () => {
      await apiFetch(`/categories/${categoryId}`, { method: 'DELETE' });
    }, 'Categoria excluída com sucesso');
  };

  const criarUsuario = async () => {
    if (!novoUsuario.name || !novoUsuario.email || !novoUsuario.password) {
      setErrorMessage('Preencha nome, e-mail e senha do novo usuário');
      return;
    }

    await runMutation(async () => {
      await apiFetch('/users', {
        method: 'POST',
        body: JSON.stringify(novoUsuario)
      });
      setNovoUsuario(emptyNovoUsuario());
    }, 'Usuário criado com sucesso');
  };

  const atualizarUsuario = async (userId, payload, successText) => {
    await runMutation(async () => {
      await apiFetch(`/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      setSenhaResetUsuario((current) => ({ ...current, [userId]: '' }));
    }, successText);
  };

  const excluirUsuarioPlataforma = async (userId) => {
    await runMutation(async () => {
      await apiFetch(`/platform/users/${userId}`, { method: 'DELETE' });
    }, 'Usuário removido da plataforma com sucesso');
  };

  const excluirTenantPlataforma = async (tenantId, tenantSlug) => {
    const confirmation = window.prompt(`Para excluir este tenant, digite o slug exatamente: ${tenantSlug}`);

    if (!confirmation) {
      return;
    }

    await runMutation(async () => {
      await apiFetch(`/platform/tenants/${tenantId}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmSlug: confirmation })
      });
    }, 'Tenant removido com sucesso');
  };

  const iniciarEdicaoTenant = (tenant) => {
    setEditingTenantId(String(tenant.id));
    setEditTenantForm({
      firstName: tenant.name || '',
      lastName: '',
      company: tenant.name || '',
      plan: normalizeTenantPlan(tenant.plan) || 'Starter',
      status: normalizeTenantStatus(tenant.subscription_status) || 'active',
      phone: '',
      email: '',
      createAdminUser: false,
      adminEmail: '',
      adminPassword: ''
    });
    clearMessages();
  };

  const cancelarEdicaoTenant = () => {
    setEditingTenantId(null);
    setEditTenantForm(emptyEditTenantForm());
    clearMessages();
  };

  const salvarTenant = async () => {
    if (!editingTenantId) {
      return;
    }

    const firstName = editTenantForm.firstName.trim();
    const lastName = editTenantForm.lastName.trim();
    const company = editTenantForm.company.trim();
    const plan = normalizeTenantPlan(editTenantForm.plan);
    const status = normalizeTenantStatus(editTenantForm.status);
    const phone = editTenantForm.phone.trim();
    const email = editTenantForm.email.trim();
    const createAdminUser = editTenantForm.createAdminUser === true;
    const adminEmail = editTenantForm.adminEmail.trim().toLowerCase();
    const adminPassword = editTenantForm.adminPassword.trim();

    if (!firstName || !plan || !status || !phone || !email) {
      setErrorMessage('Preencha os campos obrigatórios: Nome, Tipo de licença, Status, Telefone e Email');
      return;
    }

    if (createAdminUser && !adminEmail) {
      setErrorMessage('Preencha o e-mail do usuário admin');
      return;
    }

    if (createAdminUser && !adminPassword) {
      setErrorMessage('Preencha a senha do usuário admin');
      return;
    }

    const ownerName = `${firstName} ${lastName}`.trim();
    const tenantName = company || ownerName;
    const tenantSlug = normalizeTenantSlug(company || ownerName || email.split('@')[0]);

    if (!tenantName || !tenantSlug) {
      setErrorMessage('Não foi possível gerar os dados do tenant. Revise os campos informados.');
      return;
    }

    const payload = {
      name: tenantName,
      slug: tenantSlug,
      plan,
      subscriptionStatus: status,
      ownerName,
      contactPhone: phone,
      contactEmail: email,
      createAdminUser,
      adminName: ownerName || firstName,
      adminEmail: createAdminUser ? adminEmail : undefined,
      adminPassword: createAdminUser ? adminPassword : undefined
    };

    await runMutation(async () => {
      await apiFetch(`/platform/tenants/${editingTenantId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      setEditingTenantId(null);
      setEditTenantForm(emptyEditTenantForm());
    }, 'Tenant atualizado com sucesso');
  };

  const criarTenantComFormulario = async () => {
    const firstName = createTenantForm.firstName.trim();
    const lastName = createTenantForm.lastName.trim();
    const company = createTenantForm.company.trim();
    const plan = normalizeTenantPlan(createTenantForm.plan);
    const status = normalizeTenantStatus(createTenantForm.status);
    const phone = createTenantForm.phone.trim();
    const email = createTenantForm.email.trim();
    const createAdminUser = createTenantForm.createAdminUser === true;
    const adminEmail = createTenantForm.adminEmail.trim().toLowerCase();
    const adminPassword = createTenantForm.adminPassword.trim();

    if (!firstName || !plan || !status || !phone || !email) {
      setErrorMessage('Preencha os campos obrigatórios: Nome, Tipo de licença, Status, Telefone e Email');
      return;
    }

    if (createAdminUser && !adminEmail) {
      setErrorMessage('Preencha o e-mail do usuário admin');
      return;
    }

    if (createAdminUser && !adminPassword) {
      setErrorMessage('Preencha a senha do usuário admin');
      return;
    }

    const ownerName = `${firstName} ${lastName}`.trim();
    const tenantName = company || ownerName;
    const tenantSlug = normalizeTenantSlug(company || ownerName || email.split('@')[0]);

    if (!tenantName || !tenantSlug) {
      setErrorMessage('Não foi possível gerar os dados do tenant. Revise os campos informados.');
      return;
    }

    await runMutation(async () => {
      await apiFetch('/platform/tenants', {
        method: 'POST',
        body: JSON.stringify({
          name: tenantName,
          slug: tenantSlug,
          plan,
          subscriptionStatus: status,
          ownerName,
          contactPhone: phone,
          contactEmail: email,
          createAdminUser,
          adminName: ownerName || firstName,
          adminEmail: createAdminUser ? adminEmail : undefined,
          adminPassword: createAdminUser ? adminPassword : undefined
        })
      });

      setCreateTenantForm(emptyCreateTenantForm());
      setShowCreateTenantForm(false);
    }, 'Tenant criado com sucesso');
  };

  const updateCreateTenantLicense = (licenseType) => {
    setCreateTenantForm((current) => {
      const nextConfig = tenantConfigFromLicenseType(licenseType, current.status);
      return {
        ...current,
        plan: nextConfig.plan,
        status: nextConfig.status
      };
    });
  };

  const updateEditTenantLicense = (licenseType) => {
    setEditTenantForm((current) => {
      const nextConfig = tenantConfigFromLicenseType(licenseType, current.status);
      return {
        ...current,
        plan: nextConfig.plan,
        status: nextConfig.status
      };
    });
  };

  const excluirAdminPlataforma = async (adminId, adminEmail) => {
    const confirmation = window.prompt(`Para excluir este super admin, digite o e-mail exatamente: ${adminEmail}`);

    if (!confirmation) {
      return;
    }

    await runMutation(async () => {
      await apiFetch(`/platform/admins/${adminId}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmEmail: confirmation })
      });
    }, 'Administrador de plataforma removido com sucesso');
  };

  const relatorio = useMemo(() => {
    const comprasMes = compras.filter((compra) => compra.mes === mesRelatorio);
    const contasMes = contas.filter((conta) => getRecordMonth(conta) === mesRelatorio);
    const entradasMes = entradas.filter((entrada) => getRecordMonth(entrada) === mesRelatorio);
    const lazerMes = lazer.filter((item) => getRecordMonth(item) === mesRelatorio);
    const investimentosMes = investimentos.filter((investimento) => getRecordMonth(investimento) === mesRelatorio);
    const manutencoesMes = manutencoes.filter((manutencao) => manutencao.data.slice(0, 7) === mesRelatorio);

    const totalEntradas = entradasMes.reduce((acc, entrada) => acc + Number(entrada.valor), 0);
    const totalEntradasProjetado = entradasMes.filter((i) => i.status === 'planned').reduce((acc, item) => acc + Number(item.valor), 0);
    const totalEntradasEfetivado = entradasMes.filter((i) => i.status === 'executed' || !i.status).reduce((acc, item) => acc + Number(item.valor), 0);
    const totalCompras = comprasMes.reduce((acc, compra) => acc + Number(compra.valor), 0);
    const totalContas = contasMes.reduce((acc, conta) => acc + Number(conta.valor), 0);
    const totalContasProjetado = contasMes.filter((i) => i.status === 'planned').reduce((acc, item) => acc + Number(item.valor), 0);
    const totalContasEfetivado = contasMes.filter((i) => i.status === 'executed' || !i.status).reduce((acc, item) => acc + Number(item.valor), 0);
    const totalLazer = lazerMes.reduce((acc, item) => acc + Number(item.valor), 0);
    const totalLazerProjetado = lazerMes.filter((i) => i.status === 'planned').reduce((acc, item) => acc + Number(item.valor), 0);
    const totalLazerEfetivado = lazerMes.filter((i) => i.status === 'executed' || !i.status).reduce((acc, item) => acc + Number(item.valor), 0);
    const totalInvestimentos = investimentosMes.reduce((acc, investimento) => acc + Number(investimento.valor), 0);
    const totalInvestimentosProjetado = investimentosMes.filter((i) => i.status === 'planned').reduce((acc, item) => acc + Number(item.valor), 0);
    const totalInvestimentosEfetivado = investimentosMes.filter((i) => i.status === 'executed' || !i.status).reduce((acc, item) => acc + Number(item.valor), 0);
    const totalManutencoes = manutencoesMes.reduce((acc, manutencao) => acc + Number(manutencao.valor), 0);
    const totalManutencoesProjetado = manutencoesMes.filter((i) => i.status === 'planned').reduce((acc, item) => acc + Number(item.valor), 0);
    const totalManutencoesEfetivado = manutencoesMes.filter((i) => i.status === 'executed' || !i.status).reduce((acc, item) => acc + Number(item.valor), 0);
    const totalSaidas = totalCompras + totalContas + totalLazer + totalInvestimentos + totalManutencoes;
    const saldoMes = totalEntradas - totalSaidas;

    const contasPorTipo = contasMes.reduce((acc, conta) => {
      acc[conta.tipo] = (acc[conta.tipo] || 0) + Number(conta.valor);
      return acc;
    }, {});

    const investimentosPorTipo = investimentosMes.reduce((acc, investimento) => {
      const key = investimento.descricao || investimento.tipo || 'Outros';
      acc[key] = (acc[key] || 0) + Number(investimento.valor);
      return acc;
    }, {});

    return {
      comprasMes,
      contasMes,
      entradasMes,
      lazerMes,
      investimentosMes,
      manutencoesMes,
      totalEntradasProjetado,
      totalEntradasEfetivado,
      totalLazerProjetado,
      totalLazerEfetivado,
      totalInvestimentosProjetado,
      totalInvestimentosEfetivado,
      totalManutencoesProjetado,
      totalManutencoesEfetivado,
      totalContasProjetado,
      totalContasEfetivado,
      contasPorTipo,
      investimentosPorTipo,
      totalEntradas,
      totalCompras,
      totalContas,
      totalLazer,
      totalInvestimentos,
      totalManutencoes,
      totalSaidas,
      saldoMes
    };
  }, [compras, contas, entradas, lazer, investimentos, manutencoes, mesRelatorio]);

  const resumoMensal = useMemo(() => {
    const contasMes = contas.filter((conta) => getRecordMonth(conta) === mesRelatorio);
    const entradasMes = entradas.filter((entrada) => getRecordMonth(entrada) === mesRelatorio);
    const investimentosMes = investimentos.filter((investimento) => getRecordMonth(investimento) === mesRelatorio);
    const manutencoesMes = manutencoes.filter((manutencao) => manutencao.data.slice(0, 7) === mesRelatorio);
    const estoqueMes = estoque.filter((item) => String(item.updated_at || '').slice(0, 7) === mesRelatorio);

    const tenantsExpiring = (platformTenants || []).filter((t) => {
      const dateStr = t.trial_expires_at || t.subscription_expires_at || t.expires_at || t.trialExpiresAt || t.subscription_expires || null;
      if (!dateStr) return false;
      // normalize to YYYY-MM
      const month = String(dateStr).slice(0, 7);
      return month === mesRelatorio;
    }).length;

    const tenantsInactive = (platformTenants || []).filter((t) => normalizeTenantStatus(t.subscription_status || t.status || t.subscriptionStatus) === 'inactive').length;

    return {
      contas: contasMes.length,
      entradas: entradasMes.length,
      investimentos: investimentosMes.length,
      manutencoes: manutencoesMes.length,
      estoque: estoqueMes.length,
      tenantsExpiring,
      tenantsInactive
    };
  }, [contas, entradas, investimentos, manutencoes, estoque, mesRelatorio, platformTenants]);

  const tenantPlanOptions = useMemo(() => {
    const options = Array.from(new Set(
      platformTenants
        .map((tenant) => String(tenant.plan || '').trim())
        .filter(Boolean)
    ));

    return options.sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [platformTenants]);

  const tenantStatusOptions = useMemo(() => {
    const options = Array.from(new Set(
      platformTenants
        .map((tenant) => String(tenant.subscription_status || '').trim())
        .filter(Boolean)
    ));

    return options.sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [platformTenants]);

  const filteredPlatformTenants = useMemo(() => {
    const normalizedSearch = tenantSearchTerm.trim().toLowerCase();

    const filtered = platformTenants.filter((tenant) => {
      const plan = String(tenant.plan || '').trim();
      const status = String(tenant.subscription_status || '').trim();
      const name = String(tenant.name || '').toLowerCase();
      const slug = String(tenant.slug || '').toLowerCase();

      if (tenantPlanFilter !== 'all' && plan !== tenantPlanFilter) {
        return false;
      }

      if (tenantStatusFilter !== 'all' && status !== tenantStatusFilter) {
        return false;
      }

      if (normalizedSearch && !name.includes(normalizedSearch) && !slug.includes(normalizedSearch)) {
        return false;
      }

      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const valueA = String(a[tenantSortConfig.key] || '').toLowerCase();
      const valueB = String(b[tenantSortConfig.key] || '').toLowerCase();
      const compare = valueA.localeCompare(valueB, 'pt-BR');
      return tenantSortConfig.direction === 'asc' ? compare : -compare;
    });

    return sorted;
  }, [platformTenants, tenantPlanFilter, tenantStatusFilter, tenantSearchTerm, tenantSortConfig]);

  const toggleTenantSort = (key) => {
    setTenantSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }

      return { key, direction: 'asc' };
    });
  };

  const sortIndicator = (key) => {
    if (tenantSortConfig.key !== key) {
      return '↕';
    }

    return tenantSortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const editingTenantHasAdmin = useMemo(() => {
    if (!editingTenantId) {
      return false;
    }

    return platformUsers.some((platformUser) => (
      String(platformUser.tenantId || '') === String(editingTenantId)
      && (platformUser.role === 'admin' || platformUser.role === 'owner')
    ));
  }, [platformUsers, editingTenantId]);

  const platformUserRoleOptions = useMemo(() => {
    const options = Array.from(new Set(
      platformUsers
        .map((registeredUser) => String(registeredUser.role || '').trim())
        .filter(Boolean)
    ));

    return options.sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [platformUsers]);

  const platformUsersStatusOptions = useMemo(() => {
    const options = Array.from(new Set(
      platformUsers
        .map((registeredUser) => (registeredUser.active ? 'ativo' : 'inativo'))
        .filter(Boolean)
    ));

    return options.sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [platformUsers]);

  const filteredPlatformUsers = useMemo(() => {
    const normalizedSearch = platformUsersSearchTerm.trim().toLowerCase();

    return platformUsers.filter((registeredUser) => {
      const role = String(registeredUser.role || '').trim();
      const status = registeredUser.active ? 'ativo' : 'inativo';
      const name = String(registeredUser.name || '').toLowerCase();
      const email = String(registeredUser.email || '').toLowerCase();
      const tenantName = String(registeredUser.tenantName || '').toLowerCase();
      const tenantSlug = String(registeredUser.tenantSlug || '').toLowerCase();

      if (platformUsersRoleFilter !== 'all' && role !== platformUsersRoleFilter) {
        return false;
      }

      if (platformUsersStatusFilter !== 'all' && status !== platformUsersStatusFilter) {
        return false;
      }

      if (
        normalizedSearch
        && !name.includes(normalizedSearch)
        && !email.includes(normalizedSearch)
        && !tenantName.includes(normalizedSearch)
        && !tenantSlug.includes(normalizedSearch)
      ) {
        return false;
      }

      return true;
    });
  }, [platformUsers, platformUsersSearchTerm, platformUsersRoleFilter, platformUsersStatusFilter]);

  const platformUsersTotalPages = Math.max(1, Math.ceil(filteredPlatformUsers.length / PLATFORM_USERS_PAGE_SIZE));
  const platformUsersCurrentPage = Math.min(platformUsersPage, platformUsersTotalPages - 1);
  const paginatedPlatformUsers = filteredPlatformUsers.slice(
    platformUsersCurrentPage * PLATFORM_USERS_PAGE_SIZE,
    (platformUsersCurrentPage + 1) * PLATFORM_USERS_PAGE_SIZE
  );

  const categoriasConta = useMemo(() => {
    const custom = categorias
      .filter((categoria) => categoria.scope === 'contas')
      .map((categoria) => categoria.name);

    return Array.from(new Set(custom)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [categorias]);

  const categoriasCompras = useMemo(() => {
    const custom = categorias
      .filter((categoria) => categoria.scope === 'compras')
      .map((categoria) => categoria.name);

    return Array.from(new Set(custom)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [categorias]);

  const categoriasInvestimento = useMemo(() => {
    const custom = categorias
      .filter((categoria) => categoria.scope === 'investimentos')
      .map((categoria) => categoria.name);

    return Array.from(new Set(custom)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [categorias]);

  const categoriasEntrada = useMemo(() => {
    const custom = categorias
      .filter((categoria) => categoria.scope === 'entradas')
      .map((categoria) => categoria.name);

    return Array.from(new Set(custom)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [categorias]);

  const categoriasLazer = useMemo(() => {
    const custom = categorias
      .filter((categoria) => categoria.scope === 'lazer')
      .map((categoria) => categoria.name);

    return Array.from(new Set(custom)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [categorias]);

  const categoriasManutencao = useMemo(() => {
    const custom = categorias
      .filter((categoria) => categoria.scope === 'manutencoes' || categoria.scope === 'manutencao')
      .map((categoria) => categoria.name);

    return Array.from(new Set(custom)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [categorias]);

  const [viewerFilters, setViewerFilters] = useState({
    compras: { category: '', status: 'all', search: '' },
    contas: { category: '', status: 'all', search: '' },
    entradas: { category: '', status: 'all', search: '' },
    lazer: { category: '', status: 'all', search: '' },
    investimentos: { category: '', status: 'all', search: '' },
    manutencoes: { category: '', status: 'all', search: '' }
  });

  const updateViewerFilter = (section, key, value) => {
    setViewerFilters((current) => ({ ...current, [section]: { ...current[section], [key]: value } }));
  };

  useEffect(() => {
    if (categoriasConta.length === 0) {
      setNovaConta((current) => ({ ...current, tipo: '' }));
      return;
    }

    if (!categoriasConta.includes(novaConta.tipo)) {
      setNovaConta((current) => ({ ...current, tipo: categoriasConta[0] }));
    }
  }, [categoriasConta, novaConta.tipo]);

  useEffect(() => {
    if (categoriasInvestimento.length === 0) {
      setNovoInvestimento((current) => ({ ...current, descricao: '' }));
      return;
    }

    if (!categoriasInvestimento.includes(novoInvestimento.descricao)) {
      setNovoInvestimento((current) => ({ ...current, descricao: categoriasInvestimento[0] }));
    }
  }, [categoriasInvestimento, novoInvestimento.descricao]);

  useEffect(() => {
    if (categoriasEntrada.length === 0) {
      setNovaEntrada((current) => ({ ...current, tipo: '' }));
      return;
    }

    if (!categoriasEntrada.includes(novaEntrada.tipo)) {
      setNovaEntrada((current) => ({ ...current, tipo: categoriasEntrada[0] }));
    }
  }, [categoriasEntrada, novaEntrada.tipo]);

  const distributionItems = [
    { label: 'Compras', total: relatorio.totalCompras, className: 'bg-blue-500' },
    { label: 'Contas', total: relatorio.totalContas, className: 'bg-emerald-500' },
    { label: 'Lazer', total: relatorio.totalLazer, className: 'bg-fuchsia-500' },
    { label: 'Investimentos', total: relatorio.totalInvestimentos, className: 'bg-indigo-500' },
    { label: 'Extraordinárias', total: relatorio.totalManutencoes, className: 'bg-amber-500' }
  ];

  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="rounded-3xl border border-white/10 bg-white/5 px-8 py-6 shadow-2xl backdrop-blur">
          Carregando sessão...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(34,197,94,0.18),_transparent_32%),linear-gradient(135deg,#07111f_0%,#10203b_55%,#1e293b_100%)] text-slate-100 px-6 py-10">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="rounded-[32px] border border-emerald-400/20 bg-white/5 p-8 shadow-[0_20px_80px_rgba(0,0,0,0.35)] backdrop-blur">
            <h1 className="max-w-xl text-5xl font-black tracking-tight text-white">
              SMCustos</h1>
              <p>Controle sua Vida Financeira</p>
          </section>

          <section className="rounded-[32px] bg-[#f8f5ef] p-8 text-slate-900 shadow-[0_24px_90px_rgba(0,0,0,0.22)]">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-slate-950 p-3 text-emerald-300">
                <LogIn size={22} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Entrar</h2>
              </div>
            </div>

            <form className="mt-8 space-y-4" onSubmit={handleLogin}>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">E-mail</span>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(event) => setLoginForm((current) => ({ ...current, email: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-200"
                  placeholder="seu@email.com.br"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Senha</span>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-200"
                  placeholder="Sua senha"
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <LogIn size={18} /> {loading ? 'Entrando...' : 'Iniciar sessão'}
              </button>
            </form>

            <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">Novo por aqui?</p>
                <p className="text-xs text-slate-500">Crie sua conta e teste de forma gratuita por 7 dias.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPublicSignupForm((current) => !current)}
                className="rounded-2xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50"
              >
                {showPublicSignupForm ? 'Fechar' : 'Criar cadastro'}
              </button>
            </div>

            {showPublicSignupForm && (
              <form className="mt-5 space-y-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_14px_35px_rgba(15,23,42,0.08)]" onSubmit={handlePublicSignup}>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Cadastro com licença Starter</h3>
                  <p className="text-sm text-slate-600">Seu tenant nasce com licença Starter válida por 7 dias. Após isso, a assinatura precisa ser renovada.</p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-slate-700">Nome</span>
                    <input
                      type="text"
                      value={publicSignupForm.firstName}
                      onChange={(event) => setPublicSignupForm((current) => ({ ...current, firstName: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-200"
                      placeholder="Seu nome"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-slate-700">Sobrenome</span>
                    <input
                      type="text"
                      value={publicSignupForm.lastName}
                      onChange={(event) => setPublicSignupForm((current) => ({ ...current, lastName: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-200"
                      placeholder="Seu sobrenome"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-slate-700">Empresa</span>
                    <input
                      type="text"
                      value={publicSignupForm.company}
                      onChange={(event) => setPublicSignupForm((current) => ({ ...current, company: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-200"
                      placeholder="Nome da empresa"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-slate-700">Telefone</span>
                    <input
                      type="text"
                      value={publicSignupForm.phone}
                      onChange={(event) => setPublicSignupForm((current) => ({ ...current, phone: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-200"
                      placeholder="(00) 00000-0000"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-slate-700">E-mail</span>
                    <input
                      type="email"
                      value={publicSignupForm.email}
                      onChange={(event) => setPublicSignupForm((current) => ({ ...current, email: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-200"
                      placeholder="contato@empresa.com"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-slate-700">Senha do admin</span>
                    <input
                      type="password"
                      value={publicSignupForm.adminPassword}
                      onChange={(event) => setPublicSignupForm((current) => ({ ...current, adminPassword: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-200"
                      placeholder="Mínimo de 6 caracteres"
                    />
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <UserPlus size={18} /> {loading ? 'Criando...' : 'Criar cadastro'}
                </button>
              </form>
            )}

            {errorMessage && (
              <div className="mt-4 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                <AlertCircle size={18} className="mt-0.5" /> <ErrorMessageText message={errorMessage} />
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f4efe6_0%,#fbfaf7_46%,#f3f6fb_100%)] px-4 py-6 text-slate-900 md:px-6 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <header className="relative mb-6 overflow-hidden rounded-[36px] bg-[linear-gradient(135deg,#020617_0%,#0f172a_52%,#123047_100%)] text-white shadow-[0_24px_90px_rgba(15,23,42,0.35)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.18),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(56,189,248,0.14),transparent_26%)]" />

          <div className="relative flex flex-col gap-6 px-8 py-8 lg:flex-row lg:items-start lg:justify-between lg:px-10">
            <div className="flex-1 rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur">
              <h1 className="mt-3 max-w-3xl text-4xl font-black tracking-tight text-white">SMCustos</h1>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300">Controle Financeiro</p>
            </div>

            <div className="w-full rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur lg:max-w-sm">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Usuário ativo</p>
                <p className="mt-2 text-2xl font-bold text-white">{user.name}</p>
                <p className="text-sm text-slate-300">{user.email}</p>
                {!isPlatformAdmin && (
                  <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Licença atual</p>
                        <p className="mt-1 text-sm font-semibold text-emerald-200">{formatTenantPlanLabel(tenantPlan) || 'Starter'}</p>
                        {showLicenseCountdown && (
                          <p className="mt-1 text-xs text-slate-300">
                            {trialDaysRemaining > 0
                              ? `Restam ${trialDaysRemaining} dia(s) na licença`
                              : 'Licença encerrada'}
                          </p>
                        )}
                      </div>

                      <div className="ml-4 flex flex-col items-center justify-center">
                        <span className="text-[11px] font-bold text-slate-400 mb-1">Renove Agora</span>
                        <a
                          href={WHATSAPP_SUPPORT_URL}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Renove agora via WhatsApp"
                          className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-600 text-white shadow-sm hover:bg-emerald-500 transition"
                        >
                          <MessageSquare size={16} />
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <span className={`rounded-full px-3 py-1 text-sm font-semibold ${isAdmin ? 'bg-emerald-400/15 text-emerald-200' : 'bg-sky-400/15 text-sky-200'}`}>
                  {isPlatformAdmin ? 'Super Admin da Plataforma' : isAdmin ? 'Proprietário / Admin' : 'Usuário operacional'}
                </span>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  <LogOut size={16} /> Sair
                </button>
              </div>
            </div>
          </div>
        </header>

        {(errorMessage || successMessage) && (
          <div className="mb-6 grid gap-3">
            {errorMessage && (
              <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 shadow-sm">
                <XCircle size={18} className="mt-0.5" /> <ErrorMessageText message={errorMessage} />
              </div>
            )}
            {successMessage && (
              <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 shadow-sm">
                <CheckCircle2 size={18} className="mt-0.5" /> {successMessage}
              </div>
            )}
          </div>
        )}

        <nav className="mb-6 overflow-hidden rounded-[32px] border border-slate-200/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.95)_0%,rgba(241,245,249,0.92)_100%)] p-3 shadow-[0_18px_55px_rgba(15,23,42,0.10)] backdrop-blur">
          <div className="flex flex-wrap gap-3 rounded-[24px] border border-slate-200/80 bg-white/70 p-2">
          {(isPlatformAdmin ? platformTabs : [...tabs, ...(isAdmin ? adminTabs : [])]).map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition ${active ? 'bg-[linear-gradient(135deg,#020617_0%,#0f172a_58%,#123047_100%)] text-white shadow-[0_14px_30px_rgba(15,23,42,0.28)]' : 'border border-slate-200/80 bg-white/80 text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}
              >
                <Icon size={18} className={active ? 'text-emerald-300' : 'text-slate-500'} /> {tab.label}
              </button>
            );
          })}
          </div>
        </nav>

        <section className="grid gap-6 lg:grid-cols-[1.5fr_0.5fr]">
          <div className="rounded-[32px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.97)_0%,rgba(248,250,252,0.95)_100%)] p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
            {activeTab === 'compras' && (
              <div>
                <SectionHeader title="Compras" />
                {editingCompraId && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                    Editando uma compra existente. Altere os campos abaixo e salve as alterações.
                  </div>
                )}
                <div className="mt-6 grid gap-4 md:grid-cols-5">
                  <Field value={novaCompra.item} onChange={(value) => setNovaCompra((current) => ({ ...current, item: value }))} placeholder="Item" />
                  <Field type="number" value={novaCompra.quantidade} onChange={(value) => setNovaCompra((current) => ({ ...current, quantidade: value }))} placeholder="Quantidade" />
                  <Field type="number" value={novaCompra.valor} onChange={(value) => setNovaCompra((current) => ({ ...current, valor: value }))} placeholder="Valor" />
                  <MonthPicker value={novaCompra.mes} onChange={(value) => setNovaCompra((current) => ({ ...current, mes: value }))} />
                  <PrimaryButton icon={editingCompraId ? Pencil : PlusCircle} onClick={adicionarCompra} disabled={loading} label={editingCompraId ? 'Salvar alterações' : 'Adicionar'} />
                </div>
                {editingCompraId && (
                  <div className="mt-3">
                    <SecondaryTextButton onClick={cancelarEdicaoCompra} label="Cancelar edição" />
                  </div>
                )}
                <FilterMonth value={mesFiltroCompras} onChange={setMesFiltroCompras} />
                <div className="mt-4 flex items-center gap-3">
                  <select
                    value={viewerFilters.compras.category}
                    onChange={(e) => updateViewerFilter('compras', 'category', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="">Todas categorias</option>
                    {categoriasCompras.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={viewerFilters.compras.status}
                    onChange={(e) => updateViewerFilter('compras', 'status', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="all">Status</option>
                    <option value="planned">Projetado</option>
                    <option value="executed">Efetivado</option>
                  </select>
                  <Field value={viewerFilters.compras.search} onChange={(v) => updateViewerFilter('compras', 'search', v)} placeholder="Buscar" />
                </div>
                <DataTable
                  headers={['Item', 'Quantidade', 'Valor', 'Mês', 'Ações']}
                  rows={compras
                    .filter((compra) => compra.mes === mesFiltroCompras)
                    .filter((compra) => {
                      const f = viewerFilters.compras;
                      if (f.category && (compra.item || '') !== f.category) return false;
                      if (f.status === 'planned' && compra.status !== 'planned') return false;
                      if (f.status === 'executed' && compra.status !== 'executed') return false;
                      if (f.search && !String(compra.item || '').toLowerCase().includes(f.search.toLowerCase())) return false;
                      return true;
                    })
                    .map((compra) => [
                      compra.item,
                      compra.quantidade,
                      formatCurrency(compra.valor),
                      formatMonthLongLabel(compra.mes),
                      isAdmin ? (
                        <div key={`actions-compra-${compra.id}`} className="flex items-center gap-3">
                          <SecondaryTextButton onClick={() => iniciarEdicaoCompra(compra)} label="Editar" />
                          <DangerTextButton onClick={() => excluirRegistro(`/compras/${compra.id}`, 'Compra excluída com sucesso')} label="Excluir" />
                        </div>
                      ) : (
                        <span key={`readonly-compra-${compra.id}`} className="text-sm text-slate-400">Somente admin altera/exclui</span>
                      )
                    ])}
                  emptyMessage="Nenhuma compra registrada para este mês"
                />
              </div>
            )}

            {activeTab === 'contas' && (
              <div>
                <SectionHeader title="Contas Fixas" />
                {editingContaId && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                    Editando uma conta fixa. Altere os campos abaixo e salve as alterações.
                  </div>
                )}
                <div className="mt-6 grid gap-4 md:grid-cols-4">
                  <select
                    value={novaConta.tipo}
                    onChange={(event) => setNovaConta((current) => ({ ...current, tipo: event.target.value }))}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                  >
                    {categoriasConta.length === 0 && <option value="">Cadastre categorias na aba Categorias</option>}
                    {categoriasConta.map((categoria) => (
                      <option key={categoria} value={categoria}>{categoria}</option>
                    ))}
                  </select>
                  <Field type="number" value={novaConta.valor} onChange={(value) => setNovaConta((current) => ({ ...current, valor: value }))} placeholder="Valor" />
                  <Field type="date" value={novaConta.data} onChange={(value) => setNovaConta((current) => ({ ...current, data: value }))} />
                  <PrimaryButton icon={editingContaId ? Pencil : PlusCircle} onClick={adicionarConta} disabled={loading || categoriasConta.length === 0} label={editingContaId ? 'Salvar alterações' : 'Adicionar'} />
                </div>
                {editingContaId && (
                  <div className="mt-3">
                    <SecondaryTextButton onClick={cancelarEdicaoConta} label="Cancelar edição" />
                  </div>
                )}
                <FilterMonth value={mesFiltroContas} onChange={setMesFiltroContas} />
                <div className="mt-4 flex items-center gap-3">
                  <select
                    value={viewerFilters.contas.category}
                    onChange={(e) => updateViewerFilter('contas', 'category', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="">Todas categorias</option>
                    {categoriasConta.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={viewerFilters.contas.status}
                    onChange={(e) => updateViewerFilter('contas', 'status', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="all">Status</option>
                    <option value="planned">Projetado</option>
                    <option value="executed">Efetivado</option>
                  </select>
                  <Field value={viewerFilters.contas.search} onChange={(v) => updateViewerFilter('contas', 'search', v)} placeholder="Buscar" />
                </div>
                <DataTable
                  headers={['Tipo', 'Valor', 'Data', 'Status', 'Ações']}
                  rows={contas
                    .filter((conta) => getRecordMonth(conta) === mesFiltroContas)
                    .filter((conta) => {
                      const f = viewerFilters.contas;
                      if (f.category && conta.tipo !== f.category) return false;
                      if (f.status === 'planned' && conta.status !== 'planned') return false;
                      if (f.status === 'executed' && conta.status !== 'executed') return false;
                      if (f.search) {
                        const hay = `${conta.tipo} ${conta.nota || ''}`.toLowerCase();
                        if (!hay.includes(f.search.toLowerCase())) return false;
                      }
                      return true;
                    })
                    .map((conta) => [
                      conta.tipo,
                      formatCurrency(conta.valor),
                      formatDate(getRecordDate(conta)),
                      conta.status === 'planned' ? 'Projetado' : 'Efetivado',
                      isAdmin ? (
                        <div key={`actions-conta-${conta.id}`} className="flex items-center gap-2">
                          {conta.status === 'planned' && (
                            <ActionButton icon={CheckCircle2} onClick={() => confirmarConta(conta.id)} label="Confirmar" tone="primary" />
                          )}
                          <ActionButton icon={Pencil} onClick={() => iniciarEdicaoConta(conta)} label="Editar" tone="secondary" />
                          <ActionButton icon={Trash2} onClick={() => excluirRegistro(`/contas/${conta.id}`, 'Conta excluída com sucesso')} label="Excluir" tone="danger" />
                        </div>
                      ) : (
                        <span key={`readonly-conta-${conta.id}`} className="text-sm text-slate-400">Somente admin altera/exclui</span>
                      )
                    ])}
                  emptyMessage="Nenhuma conta registrada para este mês"
                />
              </div>
            )}

            {activeTab === 'entradas' && (
              <div>
                <SectionHeader title="Entradas" />
                {editingEntradaId && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                    Editando uma entrada financeira. Altere os campos abaixo e salve as alterações.
                  </div>
                )}
                <div className="mt-6 space-y-4">
                  <div className="grid gap-4 md:grid-cols-4">
                    <select
                      value={novaEntrada.tipo}
                      onChange={(event) => setNovaEntrada((current) => ({ ...current, tipo: event.target.value }))}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                    >
                      {categoriasEntrada.length === 0 && <option value="">Cadastre categorias na aba Categorias</option>}
                      {categoriasEntrada.map((categoria) => (
                        <option key={categoria} value={categoria}>{categoria}</option>
                      ))}
                    </select>
                    <Field type="number" value={novaEntrada.valor} onChange={(value) => setNovaEntrada((current) => ({ ...current, valor: value }))} placeholder="Valor" />
                    <Field type="date" value={novaEntrada.data} onChange={(value) => setNovaEntrada((current) => ({ ...current, data: value }))} />
                    <PrimaryButton icon={editingEntradaId ? Pencil : PlusCircle} onClick={adicionarEntrada} disabled={loading || categoriasEntrada.length === 0} label={editingEntradaId ? 'Salvar alterações' : 'Adicionar'} />
                  </div>
                  <Field value={novaEntrada.nota} onChange={(value) => setNovaEntrada((current) => ({ ...current, nota: value }))} placeholder="Nota (opcional)" />
                </div>
                {editingEntradaId && (
                  <div className="mt-3">
                    <SecondaryTextButton onClick={cancelarEdicaoEntrada} label="Cancelar edição" />
                  </div>
                )}
                <FilterMonth value={mesFiltroEntradas} onChange={setMesFiltroEntradas} />
                <div className="mt-4 flex items-center gap-3">
                  <select
                    value={viewerFilters.entradas.category}
                    onChange={(e) => updateViewerFilter('entradas', 'category', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="">Todas categorias</option>
                    {categoriasEntrada.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={viewerFilters.entradas.status}
                    onChange={(e) => updateViewerFilter('entradas', 'status', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="all">Status</option>
                    <option value="planned">Projetado</option>
                    <option value="executed">Efetivado</option>
                  </select>
                  <Field value={viewerFilters.entradas.search} onChange={(v) => updateViewerFilter('entradas', 'search', v)} placeholder="Buscar" />
                </div>
                <DataTable
                  headers={['Categoria', 'Valor', 'Nota', 'Data', 'Status', 'Ações']}
                  rows={entradas
                    .filter((entrada) => getRecordMonth(entrada) === mesFiltroEntradas)
                    .filter((entrada) => {
                      const f = viewerFilters.entradas;
                      if (f.category && entrada.tipo !== f.category) return false;
                      if (f.status === 'planned' && entrada.status !== 'planned') return false;
                      if (f.status === 'executed' && entrada.status !== 'executed') return false;
                      if (f.search) {
                        const hay = `${entrada.tipo} ${entrada.nota || ''}`.toLowerCase();
                        if (!hay.includes(f.search.toLowerCase())) return false;
                      }
                      return true;
                    })
                    .map((entrada) => [
                      entrada.tipo,
                      formatCurrency(entrada.valor),
                      entrada.nota || '-',
                      formatDate(getRecordDate(entrada)),
                      entrada.status === 'planned' ? 'Projetada' : 'Efetivada',
                      isAdmin ? (
                        <div key={`actions-entrada-${entrada.id}`} className="flex items-center gap-2">
                          {entrada.status === 'planned' && (
                            <ActionButton icon={CheckCircle2} onClick={() => confirmarEntrada(entrada.id)} label="Confirmar" tone="primary" />
                          )}
                          <ActionButton icon={Pencil} onClick={() => iniciarEdicaoEntrada(entrada)} label="Editar" tone="secondary" />
                          <ActionButton icon={Trash2} onClick={() => excluirRegistro(`/entradas/${entrada.id}`, 'Entrada excluída com sucesso')} label="Excluir" tone="danger" />
                        </div>
                      ) : (
                        <span key={`readonly-entrada-${entrada.id}`} className="text-sm text-slate-400">Somente admin altera/exclui</span>
                      )
                    ])}
                  emptyMessage="Nenhuma entrada registrada para este mês"
                />
              </div>
            )}

            {activeTab === 'lazer' && (
              <div>
                <SectionHeader title="Lazer" />
                {editingLazerId && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                    Editando uma despesa de lazer. Altere os campos abaixo e salve as alterações.
                  </div>
                )}
                <div className="mt-6 grid gap-4 md:grid-cols-4">
                  <Field value={novoLazer.descricao} onChange={(value) => setNovoLazer((current) => ({ ...current, descricao: value }))} placeholder="Descrição" />
                  <Field type="number" value={novoLazer.valor} onChange={(value) => setNovoLazer((current) => ({ ...current, valor: value }))} placeholder="Valor" />
                  <Field type="date" value={novoLazer.data} onChange={(value) => setNovoLazer((current) => ({ ...current, data: value }))} />
                  <PrimaryButton icon={editingLazerId ? Pencil : PlusCircle} onClick={adicionarLazer} disabled={loading} label={editingLazerId ? 'Salvar alterações' : 'Adicionar'} />
                </div>
                {editingLazerId && (
                  <div className="mt-3">
                    <SecondaryTextButton onClick={cancelarEdicaoLazer} label="Cancelar edição" />
                  </div>
                )}
                <FilterMonth value={mesFiltroLazer} onChange={setMesFiltroLazer} />
                <div className="mt-4 flex items-center gap-3">
                  <select
                    value={viewerFilters.lazer.category}
                    onChange={(e) => updateViewerFilter('lazer', 'category', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="">Todas categorias</option>
                    {categoriasLazer && categoriasLazer.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={viewerFilters.lazer.status}
                    onChange={(e) => updateViewerFilter('lazer', 'status', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="all">Status</option>
                    <option value="planned">Projetado</option>
                    <option value="executed">Efetivado</option>
                  </select>
                  <Field value={viewerFilters.lazer.search} onChange={(v) => updateViewerFilter('lazer', 'search', v)} placeholder="Buscar" />
                </div>
                <div className="mt-6">
                  <DataTable
                    headers={['Descrição', 'Valor', 'Data', 'Status', 'Ações']}
                    rows={lazer
                      .filter((item) => getRecordMonth(item) === mesFiltroLazer)
                      .filter((item) => {
                        const f = viewerFilters.lazer;
                        if (f.category && (item.descricao || '') !== f.category) return false;
                        if (f.status === 'planned' && item.status !== 'planned') return false;
                        if (f.status === 'executed' && item.status !== 'executed') return false;
                        if (f.search && !String(item.descricao || '').toLowerCase().includes(f.search.toLowerCase())) return false;
                        return true;
                      })
                      .map((item) => [
                        item.descricao,
                        formatCurrency(item.valor),
                        formatDate(getRecordDate(item)),
                        item.status === 'planned' ? 'Projetada' : 'Efetivada',
                        isAdmin ? (
                          <div key={`actions-lazer-${item.id}`} className="flex items-center gap-2">
                            {item.status === 'planned' && (
                              <ActionButton icon={CheckCircle2} onClick={() => confirmarLazer(item.id)} label="Confirmar" tone="primary" />
                            )}
                            <ActionButton icon={Pencil} onClick={() => iniciarEdicaoLazer(item)} label="Editar" tone="secondary" />
                            <ActionButton icon={Trash2} onClick={() => excluirRegistro(`/lazer/${item.id}`, 'Despesa de lazer excluída com sucesso')} label="Excluir" tone="danger" />
                          </div>
                        ) : (
                          <span key={`readonly-lazer-${item.id}`} className="text-sm text-slate-400">Somente admin altera/exclui</span>
                        )
                      ])}
                    emptyMessage="Nenhuma despesa registrada para este mês"
                  />
                </div>
              </div>
            )}

            {activeTab === 'manutencoes' && (
              <div>
                <SectionHeader title="Extraordinárias" />
                {editingManutencaoId && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                    Editando uma despesa extraordinária. Altere os campos abaixo e salve as alterações.
                  </div>
                )}
                <div className="mt-6 grid gap-4 md:grid-cols-4">
                  <Field value={novaManutencao.descricao} onChange={(value) => setNovaManutencao((current) => ({ ...current, descricao: value }))} placeholder="Descrição" />
                  <Field type="number" value={novaManutencao.valor} onChange={(value) => setNovaManutencao((current) => ({ ...current, valor: value }))} placeholder="Valor" />
                  <Field type="date" value={novaManutencao.data} onChange={(value) => setNovaManutencao((current) => ({ ...current, data: value }))} />
                  <PrimaryButton icon={editingManutencaoId ? Pencil : PlusCircle} onClick={adicionarManutencao} disabled={loading} label={editingManutencaoId ? 'Salvar alterações' : 'Adicionar'} />
                </div>
                {editingManutencaoId && (
                  <div className="mt-3">
                    <SecondaryTextButton onClick={cancelarEdicaoManutencao} label="Cancelar edição" />
                  </div>
                )}
                <FilterMonth value={mesFiltroManutencoes} onChange={setMesFiltroManutencoes} />
                <div className="mt-4 flex items-center gap-3">
                  <select
                    value={viewerFilters.manutencoes.category}
                    onChange={(e) => updateViewerFilter('manutencoes', 'category', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="">Todas categorias</option>
                    {categoriasManutencao && categoriasManutencao.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={viewerFilters.manutencoes.status}
                    onChange={(e) => updateViewerFilter('manutencoes', 'status', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="all">Status</option>
                    <option value="planned">Projetado</option>
                    <option value="executed">Efetivado</option>
                  </select>
                  <Field value={viewerFilters.manutencoes.search} onChange={(v) => updateViewerFilter('manutencoes', 'search', v)} placeholder="Buscar" />
                </div>
                <DataTable
                  headers={['Descrição', 'Valor', 'Data', 'Status', 'Ações']}
                  rows={manutencoes
                    .filter((manutencao) => manutencao.data.slice(0, 7) === mesFiltroManutencoes)
                    .filter((manutencao) => {
                      const f = viewerFilters.manutencoes;
                      if (f.category && (manutencao.descricao || '') !== f.category) return false;
                      if (f.status === 'planned' && manutencao.status !== 'planned') return false;
                      if (f.status === 'executed' && manutencao.status !== 'executed') return false;
                      if (f.search && !String(manutencao.descricao || '').toLowerCase().includes(f.search.toLowerCase())) return false;
                      return true;
                    })
                    .map((manutencao) => [
                      manutencao.descricao,
                      formatCurrency(manutencao.valor),
                      formatDate(manutencao.data),
                      manutencao.status === 'planned' ? 'Projetada' : 'Efetivada',
                      isAdmin ? (
                        <div key={`actions-manutencao-${manutencao.id}`} className="flex items-center gap-2">
                          {manutencao.status === 'planned' && (
                            <ActionButton icon={CheckCircle2} onClick={() => confirmarManutencao(manutencao.id)} label="Confirmar" tone="primary" />
                          )}
                          <ActionButton icon={Pencil} onClick={() => iniciarEdicaoManutencao(manutencao)} label="Editar" tone="secondary" />
                          <ActionButton icon={Trash2} onClick={() => excluirRegistro(`/manutencoes/${manutencao.id}`, 'Despesa extraordinária excluída com sucesso')} label="Excluir" tone="danger" />
                        </div>
                      ) : (
                        <span key={`readonly-manutencao-${manutencao.id}`} className="text-sm text-slate-400">Somente admin altera/exclui</span>
                      )
                    ])}
                  emptyMessage="Nenhuma manutenção registrada para este mês"
                />
              </div>
            )}

            {activeTab === 'investimentos' && (
              <div>
                <SectionHeader title="Investimentos" />
                {editingInvestimentoId && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                    Editando um investimento. Altere os campos abaixo e salve as alterações.
                  </div>
                )}
                <div className="mt-6 space-y-4">
                  <div className="grid gap-4 md:grid-cols-4">
                    <select
                      value={novoInvestimento.descricao}
                      onChange={(event) => setNovoInvestimento((current) => ({ ...current, descricao: event.target.value }))}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                    >
                      {categoriasInvestimento.length === 0 && <option value="">Cadastre categorias na aba Categorias</option>}
                      {categoriasInvestimento.map((categoria) => (
                        <option key={categoria} value={categoria}>{categoria}</option>
                      ))}
                    </select>
                    <Field type="number" value={novoInvestimento.valor} onChange={(value) => setNovoInvestimento((current) => ({ ...current, valor: value }))} placeholder="Valor" />
                    <Field type="date" value={novoInvestimento.data} onChange={(value) => setNovoInvestimento((current) => ({ ...current, data: value }))} />
                    <PrimaryButton icon={editingInvestimentoId ? Pencil : PlusCircle} onClick={adicionarInvestimento} disabled={loading || categoriasInvestimento.length === 0} label={editingInvestimentoId ? 'Salvar alterações' : 'Adicionar'} />
                  </div>
                  <Field value={novoInvestimento.nota} onChange={(value) => setNovoInvestimento((current) => ({ ...current, nota: value }))} placeholder="Descrição (opcional)" />
                </div>
                {editingInvestimentoId && (
                  <div className="mt-3">
                    <SecondaryTextButton onClick={cancelarEdicaoInvestimento} label="Cancelar edição" />
                  </div>
                )}
                <FilterMonth value={mesFiltroInvestimentos} onChange={setMesFiltroInvestimentos} />
                <div className="mt-4 flex items-center gap-3">
                  <select
                    value={viewerFilters.investimentos.category}
                    onChange={(e) => updateViewerFilter('investimentos', 'category', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="">Todas categorias</option>
                    {categoriasInvestimento.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={viewerFilters.investimentos.status}
                    onChange={(e) => updateViewerFilter('investimentos', 'status', e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none"
                  >
                    <option value="all">Status</option>
                    <option value="planned">Projetado</option>
                    <option value="executed">Efetivado</option>
                  </select>
                  <Field value={viewerFilters.investimentos.search} onChange={(v) => updateViewerFilter('investimentos', 'search', v)} placeholder="Buscar" />
                </div>
                <DataTable
                  headers={['Categoria', 'Valor', 'Descrição', 'Data', 'Status', 'Ações']}
                  rows={investimentos
                    .filter((investimento) => getRecordMonth(investimento) === mesFiltroInvestimentos)
                    .filter((investimento) => {
                      const f = viewerFilters.investimentos;
                      if (f.category && investimento.descricao !== f.category) return false;
                      if (f.status === 'planned' && investimento.status !== 'planned') return false;
                      if (f.status === 'executed' && investimento.status !== 'executed') return false;
                      if (f.search) {
                        const hay = `${investimento.descricao} ${investimento.nota || ''}`.toLowerCase();
                        if (!hay.includes(f.search.toLowerCase())) return false;
                      }
                      return true;
                    })
                    .map((investimento) => [
                      investimento.descricao,
                      formatCurrency(investimento.valor),
                      investimento.nota || '-',
                      formatDate(getRecordDate(investimento)),
                      investimento.status === 'planned' ? 'Projetado' : 'Efetivado',
                      isAdmin ? (
                        <div key={`actions-investimento-${investimento.id}`} className="flex items-center gap-2">
                          {investimento.status === 'planned' && (
                            <ActionButton icon={CheckCircle2} onClick={() => confirmarInvestimento(investimento.id)} label="Confirmar" tone="primary" />
                          )}
                          <ActionButton icon={Pencil} onClick={() => iniciarEdicaoInvestimento(investimento)} label="Editar" tone="secondary" />
                          <ActionButton icon={Trash2} onClick={() => excluirRegistro(`/investimentos/${investimento.id}`, 'Investimento excluído com sucesso')} label="Excluir" tone="danger" />
                        </div>
                      ) : (
                        <span key={`readonly-investimento-${investimento.id}`} className="text-sm text-slate-400">Somente admin altera/exclui</span>
                      )
                    ])}
                  emptyMessage="Nenhum investimento registrado para este mês"
                />
              </div>
            )}

            {activeTab === 'estoque' && (
              <div>
                <SectionHeader title="Estoque" />
                <DataTable
                  headers={['Item', 'Quantidade', 'Baixa']}
                  rows={estoque.map((item) => [
                    item.item,
                    item.quantidade,
                    <div key={`estoque-${item.id}`} className="flex flex-wrap items-center gap-2">
                      <input
                        type="number"
                        value={baixasEstoque[item.id] || ''}
                        onChange={(event) => setBaixasEstoque((current) => ({ ...current, [item.id]: event.target.value }))}
                        className="w-24 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-100"
                        placeholder="Qtd"
                      />
                      <button
                        type="button"
                        onClick={() => darBaixaEstoque(item.id)}
                        className="rounded-xl bg-amber-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-amber-600"
                      >
                        Dar baixa
                      </button>
                    </div>
                  ])}
                  emptyMessage="Nenhum item em estoque"
                />
              </div>
            )}

            {activeTab === 'relatorios' && (
              <div>
                <SectionHeader title="Relatórios" description="Visão mensal consolidada." />
                <FilterMonth value={mesRelatorio} onChange={setMesRelatorio} />
                <div className="mt-6 space-y-4">
                  {[
                    { label: 'Entradas', projetado: relatorio.totalEntradasProjetado || 0, efetivado: relatorio.totalEntradasEfetivado || 0 },
                    { label: 'Contas', projetado: relatorio.totalContasProjetado || 0, efetivado: relatorio.totalContasEfetivado || 0 },
                    { label: 'Lazer', projetado: relatorio.totalLazerProjetado || 0, efetivado: relatorio.totalLazerEfetivado || 0 },
                    { label: 'Investimentos', projetado: relatorio.totalInvestimentosProjetado || 0, efetivado: relatorio.totalInvestimentosEfetivado || 0 },
                    { label: 'Extraordinárias', projetado: relatorio.totalManutencoesProjetado || 0, efetivado: relatorio.totalManutencoesEfetivado || 0 }
                  ].map((cat) => {
                    const diferenca = cat.projetado - cat.efetivado;

                    return (
                      <div key={cat.label} className="flex items-center gap-4 rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-slate-600">{cat.label} Projetado</div>
                          <div className="mt-2 text-2xl font-bold text-slate-900">{formatCurrency(cat.projetado)}</div>
                        </div>

                        <div className="flex-1 text-center">
                          <div className="text-sm font-semibold text-slate-600">Efetivado</div>
                          <div className="mt-2 text-2xl font-bold text-slate-900">{formatCurrency(cat.efetivado)}</div>
                        </div>

                        <div className="flex-1 text-right">
                          <div className="text-sm font-semibold text-slate-600">Diferença</div>
                          <div className={`mt-2 text-2xl font-bold ${diferenca >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatCurrency(diferenca)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 grid gap-6 xl:grid-cols-2">
                  <div>
                    <div className="rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)] mb-4">
                      <h3 className="text-lg font-bold">Gastos por tipo de conta</h3>
                      <div className="mt-4 space-y-3">
                        {Object.keys(relatorio.contasPorTipo).length === 0 && <p className="text-sm text-slate-500">Sem contas no mês selecionado.</p>}
                        {Object.entries(relatorio.contasPorTipo).map(([tipo, valor]) => {
                          const width = relatorio.totalContas > 0 ? valueToPercent(valor, relatorio.totalContas) : 0;

                          return (
                            <div key={tipo}>
                              <div className="mb-1 flex items-center justify-between text-sm font-semibold text-slate-700">
                                <span>{tipo}</span>
                                <span>{formatCurrency(valor)}</span>
                              </div>
                              <div className="h-3 overflow-hidden rounded-full bg-slate-200">
                                <div className="h-full rounded-full bg-slate-900" style={{ width: `${width}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                      <h3 className="text-lg font-bold">Investimentos por tipo</h3>
                      <div className="mt-4 space-y-3">
                        {Object.keys(relatorio.investimentosPorTipo).length === 0 && <p className="text-sm text-slate-500">Sem investimentos no mês selecionado.</p>}
                        {Object.entries(relatorio.investimentosPorTipo).map(([tipo, valor]) => {
                          const width = relatorio.totalInvestimentos > 0 ? valueToPercent(valor, relatorio.totalInvestimentos) : 0;

                          return (
                            <div key={tipo}>
                              <div className="mb-1 flex items-center justify-between text-sm font-semibold text-slate-700">
                                <span>{tipo}</span>
                                <span>{formatCurrency(valor)}</span>
                              </div>
                              <div className="h-3 overflow-hidden rounded-full bg-slate-200">
                                <div className="h-full rounded-full bg-indigo-600" style={{ width: `${width}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                    <h3 className="text-lg font-bold">Distribuição das saídas</h3>
                    <div className="mt-4 space-y-4">
                      {distributionItems.map((item) => {
                        const width = relatorio.totalSaidas > 0 ? valueToPercent(item.total, relatorio.totalSaidas) : 0;

                        return (
                          <div key={item.label}>
                            <div className="mb-1 flex items-center justify-between text-sm font-semibold text-slate-700">
                              <span>{item.label}</span>
                              <span>{width.toFixed(1)}%</span>
                            </div>
                            <div className="h-4 overflow-hidden rounded-full bg-slate-200">
                              <div className={`h-full rounded-full ${item.className}`} style={{ width: `${width}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'admin' && isAdmin && (
              <div>
                <SectionHeader title="Portal Administrativo"/>

                <div className="mt-6 grid gap-6">
                  <div className="rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                    <div className="flex items-center gap-3">
                      <UserPlus size={20} />
                      <h3 className="text-lg font-bold">Criar usuário operacional</h3>
                    </div>
                    <div className="mt-4 grid gap-3">
                      <Field value={novoUsuario.name} onChange={(value) => setNovoUsuario((current) => ({ ...current, name: value }))} placeholder="Nome" />
                      <Field type="email" value={novoUsuario.email} onChange={(value) => setNovoUsuario((current) => ({ ...current, email: value }))} placeholder="E-mail" />
                      <Field type="password" value={novoUsuario.password} onChange={(value) => setNovoUsuario((current) => ({ ...current, password: value }))} placeholder="Senha temporária" />
                      <PrimaryButton icon={Users} onClick={criarUsuario} disabled={loading} label="Criar usuário" />
                    </div>
                  </div>
                </div>

                <div className="mt-6 rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                  <div className="border-b border-slate-200 px-5 py-4">
                    <h3 className="text-lg font-bold">Usuários cadastrados</h3>
                  </div>
                  <div className="overflow-x-auto px-5 py-4">
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="pb-3 pr-4 font-semibold">Nome</th>
                          <th className="pb-3 pr-4 font-semibold">E-mail</th>
                          <th className="pb-3 pr-4 font-semibold">Papel</th>
                          <th className="pb-3 pr-4 font-semibold">Status</th>
                          <th className="pb-3 pr-4 font-semibold">Senha</th>
                          <th className="pb-3 pr-4 font-semibold">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usuarios.map((registeredUser) => (
                          <tr key={registeredUser.id} className="border-t border-slate-100 align-top">
                            <td className="py-4 pr-4 font-semibold text-slate-800">{registeredUser.name}</td>
                            <td className="py-4 pr-4 text-slate-600">{registeredUser.email}</td>
                            <td className="py-4 pr-4">
                              <span className={`rounded-full px-3 py-1 text-xs font-bold ${registeredUser.role === 'admin' ? 'bg-emerald-100 text-emerald-800' : 'bg-sky-100 text-sky-800'}`}>
                                {registeredUser.role === 'admin' ? 'Admin' : 'Usuário'}
                              </span>
                            </td>
                            <td className="py-4 pr-4">
                              <span className={`rounded-full px-3 py-1 text-xs font-bold ${registeredUser.active ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-700'}`}>
                                {registeredUser.active ? 'Ativo' : 'Inativo'}
                              </span>
                            </td>
                            <td className="py-4 pr-4">
                              <input
                                type="password"
                                value={senhaResetUsuario[registeredUser.id] || ''}
                                onChange={(event) => setSenhaResetUsuario((current) => ({ ...current, [registeredUser.id]: event.target.value }))}
                                className="w-40 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:border-slate-900 focus:ring-4 focus:ring-slate-200"
                                placeholder="Nova senha"
                              />
                            </td>
                            <td className="py-4 pr-4">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => atualizarUsuario(registeredUser.id, { active: !registeredUser.active }, registeredUser.active ? 'Usuário desativado' : 'Usuário reativado')}
                                  disabled={registeredUser.role === 'admin' || loading}
                                  className="rounded-xl border border-slate-200 px-3 py-2 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {registeredUser.active ? 'Desativar' : 'Ativar'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => atualizarUsuario(registeredUser.id, { password: senhaResetUsuario[registeredUser.id] }, 'Senha atualizada')}
                                  disabled={!senhaResetUsuario[registeredUser.id] || loading}
                                  className="rounded-xl bg-slate-950 px-3 py-2 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Resetar senha
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'categories' && isAdmin && (
              <div>
                <SectionHeader title="Gerenciamento de Categorias" description="Cadastre categorias reutilizáveis para contas e investimentos." />

                <div className="mt-6 space-y-6">
                  <div className="rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                    <h3 className="text-lg font-bold">Categorias de Contas</h3>
                    <div className="mt-4 flex flex-col gap-3 md:flex-row">
                      <Field value={novaCategoriaConta} onChange={setNovaCategoriaConta} placeholder="Nome da categoria" />
                      <PrimaryButton icon={PlusCircle} onClick={() => adicionarCategoria('contas', novaCategoriaConta)} disabled={loading} label="Adicionar" />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {categorias.filter((categoria) => categoria.scope === 'contas').map((categoria) => (
                        <button
                          key={categoria.id}
                          type="button"
                          onClick={() => excluirCategoria(categoria.id)}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                        >
                          {categoria.name} <Trash2 size={14} />
                        </button>
                      ))}
                      {categorias.filter((categoria) => categoria.scope === 'contas').length === 0 && (
                        <p className="text-sm text-slate-500">Nenhuma categoria cadastrada.</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                    <h3 className="text-lg font-bold">Categorias de Investimentos</h3>
                    <div className="mt-4 flex flex-col gap-3 md:flex-row">
                      <Field value={novaCategoriaInvestimento} onChange={setNovaCategoriaInvestimento} placeholder="Nome da categoria" />
                      <PrimaryButton icon={PlusCircle} onClick={() => adicionarCategoria('investimentos', novaCategoriaInvestimento)} disabled={loading} label="Adicionar" />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {categorias.filter((categoria) => categoria.scope === 'investimentos').map((categoria) => (
                        <button
                          key={categoria.id}
                          type="button"
                          onClick={() => excluirCategoria(categoria.id)}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                        >
                          {categoria.name} <Trash2 size={14} />
                        </button>
                      ))}
                      {categorias.filter((categoria) => categoria.scope === 'investimentos').length === 0 && (
                        <p className="text-sm text-slate-500">Nenhuma categoria cadastrada.</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                    <h3 className="text-lg font-bold">Categorias de Entradas</h3>
                    <div className="mt-4 flex flex-col gap-3 md:flex-row">
                      <Field value={novaCategoriaEntrada} onChange={setNovaCategoriaEntrada} placeholder="Nome da categoria" />
                      <PrimaryButton icon={PlusCircle} onClick={() => adicionarCategoria('entradas', novaCategoriaEntrada)} disabled={loading} label="Adicionar" />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {categorias.filter((categoria) => categoria.scope === 'entradas').map((categoria) => (
                        <button
                          key={categoria.id}
                          type="button"
                          onClick={() => excluirCategoria(categoria.id)}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                        >
                          {categoria.name} <Trash2 size={14} />
                        </button>
                      ))}
                      {categorias.filter((categoria) => categoria.scope === 'entradas').length === 0 && (
                        <p className="text-sm text-slate-500">Nenhuma categoria cadastrada.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'platform' && isPlatformAdmin && (
              <div>
                <SectionHeader title="Administração da Plataforma" description="Gerencie empresas (tenants) e usuários globais da ferramenta." />

                <div className="mt-6 rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-lg font-bold">Criar tenant</h3>
                    {!showCreateTenantForm ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateTenantForm(true);
                          clearMessages();
                        }}
                        className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-3 py-2 font-semibold text-white transition hover:bg-slate-800"
                      >
                        <PlusCircle size={14} /> Novo tenant
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateTenantForm(false);
                          setCreateTenantForm(emptyCreateTenantForm());
                          clearMessages();
                        }}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>

                  {showCreateTenantForm && (
                    <>
                      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        <Field value={createTenantForm.firstName} onChange={(value) => setCreateTenantForm((current) => ({ ...current, firstName: value }))} placeholder="Nome *" />
                        <Field value={createTenantForm.lastName} onChange={(value) => setCreateTenantForm((current) => ({ ...current, lastName: value }))} placeholder="Sobrenome" />
                        <Field value={createTenantForm.company} onChange={(value) => setCreateTenantForm((current) => ({ ...current, company: value }))} placeholder="Empresa" />
                        <label className="block">
                          <span className="mb-2 block text-sm font-semibold text-slate-700">Tipo de licença</span>
                          <select
                            value={licenseTypeFromTenant(createTenantForm.plan, createTenantForm.status)}
                            onChange={(event) => updateCreateTenantLicense(event.target.value)}
                            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                          >
                            {TENANT_LICENSE_TYPES.map((licenseType) => (
                              <option key={licenseType.value} value={licenseType.value}>{licenseType.label}</option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          <span className="mb-2 block text-sm font-semibold text-slate-700">Status</span>
                          <select
                            value={normalizeTenantStatus(createTenantForm.status) === 'trial' ? 'active' : createTenantForm.status}
                            onChange={(event) => setCreateTenantForm((current) => ({ ...current, status: event.target.value }))}
                            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                          >
                            {TENANT_STATUSES.map((statusOption) => (
                              <option key={statusOption.value} value={statusOption.value}>{statusOption.label}</option>
                            ))}
                          </select>
                        </label>
                        <Field value={createTenantForm.phone} onChange={(value) => setCreateTenantForm((current) => ({ ...current, phone: value }))} placeholder="Telefone *" />
                        <Field type="email" value={createTenantForm.email} onChange={(value) => setCreateTenantForm((current) => ({ ...current, email: value }))} placeholder="Email *" />
                      </div>
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                        <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <input
                            type="checkbox"
                            checked={createTenantForm.createAdminUser}
                            onChange={(event) => setCreateTenantForm((current) => ({ ...current, createAdminUser: event.target.checked }))}
                            className="h-4 w-4 rounded border-slate-300"
                          />
                          Criar usuário admin no cadastro do tenant
                        </label>

                        {createTenantForm.createAdminUser && (
                          <div className="mt-3 grid gap-3 md:grid-cols-2">
                            <Field
                              type="email"
                              value={createTenantForm.adminEmail}
                              onChange={(value) => setCreateTenantForm((current) => ({ ...current, adminEmail: value }))}
                              placeholder="E-mail do admin *"
                            />
                            <Field
                              type="password"
                              value={createTenantForm.adminPassword}
                              onChange={(value) => setCreateTenantForm((current) => ({ ...current, adminPassword: value }))}
                              placeholder="Senha do admin *"
                            />
                          </div>
                        )}
                      </div>
                      <p className="mt-3 text-xs text-slate-500">Campos com * são obrigatórios. Sobrenome e Empresa são opcionais.</p>
                      <div className="mt-4">
                        <PrimaryButton
                          icon={PlusCircle}
                          onClick={criarTenantComFormulario}
                          disabled={loading}
                          label="Criar tenant"
                        />
                      </div>
                    </>
                  )}
                </div>

                {editingTenantId && (
                  <div className="mt-6 rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-lg font-bold">Editar tenant</h3>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-4">
                      <Field value={editTenantForm.firstName} onChange={(value) => setEditTenantForm((current) => ({ ...current, firstName: value }))} placeholder="Nome *" />
                      <Field value={editTenantForm.lastName} onChange={(value) => setEditTenantForm((current) => ({ ...current, lastName: value }))} placeholder="Sobrenome" />
                      <Field value={editTenantForm.company} onChange={(value) => setEditTenantForm((current) => ({ ...current, company: value }))} placeholder="Empresa" />
                      <label className="block">
                        <span className="mb-2 block text-sm font-semibold text-slate-700">Tipo de licença</span>
                        <select
                          value={licenseTypeFromTenant(editTenantForm.plan, editTenantForm.status)}
                          onChange={(event) => updateEditTenantLicense(event.target.value)}
                          className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                        >
                          {TENANT_LICENSE_TYPES.map((licenseType) => (
                            <option key={licenseType.value} value={licenseType.value}>{licenseType.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="block">
                        <span className="mb-2 block text-sm font-semibold text-slate-700">Status</span>
                        <select
                          value={normalizeTenantStatus(editTenantForm.status) === 'trial' ? 'active' : editTenantForm.status}
                          onChange={(event) => setEditTenantForm((current) => ({ ...current, status: event.target.value }))}
                          className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                        >
                          {TENANT_STATUSES.map((statusOption) => (
                            <option key={statusOption.value} value={statusOption.value}>{statusOption.label}</option>
                          ))}
                        </select>
                      </label>
                      <Field value={editTenantForm.phone} onChange={(value) => setEditTenantForm((current) => ({ ...current, phone: value }))} placeholder="Telefone *" />
                      <Field type="email" value={editTenantForm.email} onChange={(value) => setEditTenantForm((current) => ({ ...current, email: value }))} placeholder="Email *" />
                    </div>
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                        {editingTenantHasAdmin ? 'Ação detectada: reset de admin existente' : 'Ação detectada: criação de admin para o tenant'}
                      </p>
                      <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <input
                          type="checkbox"
                          checked={editTenantForm.createAdminUser}
                          onChange={(event) => setEditTenantForm((current) => ({ ...current, createAdminUser: event.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                        Criar ou resetar usuário admin do tenant
                      </label>

                      {editTenantForm.createAdminUser && (
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                          <Field
                            type="email"
                            value={editTenantForm.adminEmail}
                            onChange={(value) => setEditTenantForm((current) => ({ ...current, adminEmail: value }))}
                            placeholder="E-mail do admin *"
                          />
                          <Field
                            type="password"
                            value={editTenantForm.adminPassword}
                            onChange={(value) => setEditTenantForm((current) => ({ ...current, adminPassword: value }))}
                            placeholder="Senha do admin *"
                          />
                        </div>
                      )}
                    </div>
                    <p className="mt-3 text-xs text-slate-500">Campos com * são obrigatórios. Sobrenome e Empresa são opcionais.</p>
                    <div className="mt-4 flex items-center gap-3">
                      <PrimaryButton
                        icon={PlusCircle}
                        onClick={salvarTenant}
                        disabled={loading}
                        label="Salvar alterações"
                      />
                      <button
                        type="button"
                        onClick={cancelarEdicaoTenant}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        Cancelar edição
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-6 rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                  <div className="border-b border-slate-200 px-5 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-lg font-bold">Tenants cadastrados</h3>
                      <span className="text-sm font-semibold text-slate-600">
                        {filteredPlatformTenants.length} de {platformTenants.length} tenants
                      </span>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto]">
                      <input
                        type="text"
                        value={tenantSearchTerm}
                        onChange={(event) => setTenantSearchTerm(event.target.value)}
                        placeholder="Buscar por nome ou slug"
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                      />
                      <select
                        value={tenantPlanFilter}
                        onChange={(event) => setTenantPlanFilter(event.target.value)}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                      >
                        <option value="all">Todas as licenças</option>
                        {tenantPlanOptions.map((plan) => (
                          <option key={plan} value={plan}>{formatTenantPlanLabel(plan)}</option>
                        ))}
                      </select>
                      <select
                        value={tenantStatusFilter}
                        onChange={(event) => setTenantStatusFilter(event.target.value)}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                      >
                        <option value="all">Todos os status</option>
                        {tenantStatusOptions.map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          setTenantSearchTerm('');
                          setTenantPlanFilter('all');
                          setTenantStatusFilter('all');
                          setTenantSortConfig(defaultTenantSort());
                        }}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        Limpar filtros
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto px-5 py-4">
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="pb-3 pr-4 font-semibold">
                            <button
                              type="button"
                              onClick={() => toggleTenantSort('name')}
                              className="inline-flex items-center gap-1 transition hover:text-slate-800"
                            >
                              Nome <span>{sortIndicator('name')}</span>
                            </button>
                          </th>
                          <th className="pb-3 pr-4 font-semibold">Slug</th>
                          <th className="pb-3 pr-4 font-semibold">
                            <button
                              type="button"
                              onClick={() => toggleTenantSort('plan')}
                              className="inline-flex items-center gap-1 transition hover:text-slate-800"
                            >
                              Licença <span>{sortIndicator('plan')}</span>
                            </button>
                          </th>
                          <th className="pb-3 pr-4 font-semibold">
                            <button
                              type="button"
                              onClick={() => toggleTenantSort('subscription_status')}
                              className="inline-flex items-center gap-1 transition hover:text-slate-800"
                            >
                              Status <span>{sortIndicator('subscription_status')}</span>
                            </button>
                          </th>
                          <th className="pb-3 pr-4 font-semibold">Usuários</th>
                          <th className="pb-3 pr-4 font-semibold">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPlatformTenants.map((tenant) => (
                          <tr key={tenant.id} className="border-t border-slate-100 align-top">
                            <td className="py-4 pr-4 font-semibold text-slate-800">{tenant.name}</td>
                            <td className="py-4 pr-4 text-slate-600">{tenant.slug}</td>
                            <td className="py-4 pr-4 text-slate-600">{formatTenantPlanLabel(tenant.plan)}</td>
                            <td className="py-4 pr-4 text-slate-600">{tenant.subscription_status || '-'}</td>
                            <td className="py-4 pr-4 text-slate-600">{tenant.users_count ?? 0}</td>
                            <td className="py-4 pr-4">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => iniciarEdicaoTenant(tenant)}
                                  disabled={loading}
                                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Pencil size={14} /> Editar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => excluirTenantPlataforma(tenant.id, tenant.slug)}
                                  disabled={tenant.is_protected || loading}
                                  className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Trash2 size={14} /> {tenant.is_protected ? 'Tenant protegido' : 'Excluir tenant'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {filteredPlatformTenants.length === 0 && (
                          <tr>
                            <td className="py-5 text-center text-slate-500" colSpan={6}>Nenhum tenant encontrado com os filtros atuais</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-6 rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                  <div className="border-b border-slate-200 px-5 py-4">
                    <h3 className="text-lg font-bold">Super admins da plataforma</h3>
                  </div>
                  <div className="overflow-x-auto px-5 py-4">
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="pb-3 pr-4 font-semibold">Nome</th>
                          <th className="pb-3 pr-4 font-semibold">E-mail</th>
                          <th className="pb-3 pr-4 font-semibold">Status</th>
                          <th className="pb-3 pr-4 font-semibold">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {platformAdmins.map((platformAdmin) => {
                          const isCurrentAdmin = String(platformAdmin.id) === String(user?.id);

                          return (
                            <tr key={platformAdmin.id} className="border-t border-slate-100 align-top">
                              <td className="py-4 pr-4 font-semibold text-slate-800">{platformAdmin.name}</td>
                              <td className="py-4 pr-4 text-slate-600">{platformAdmin.email}</td>
                              <td className="py-4 pr-4 text-slate-600">{platformAdmin.active ? 'Ativo' : 'Inativo'}</td>
                              <td className="py-4 pr-4">
                                <button
                                  type="button"
                                  onClick={() => excluirAdminPlataforma(platformAdmin.id, platformAdmin.email)}
                                  disabled={isCurrentAdmin || loading}
                                  className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Trash2 size={14} /> {isCurrentAdmin ? 'Conta logada protegida' : 'Excluir admin'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {platformAdmins.length === 0 && (
                          <tr>
                            <td className="py-5 text-center text-slate-500" colSpan={4}>Nenhum super admin encontrado</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-6 rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] shadow-[0_12px_35px_rgba(15,23,42,0.05)]">
                  <div className="border-b border-slate-200 px-5 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-lg font-bold">Usuários da plataforma</h3>
                      <span className="text-sm font-semibold text-slate-600">
                        {filteredPlatformUsers.length} de {platformUsers.length} usuários
                      </span>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-[1.4fr_0.8fr_0.8fr_auto]">
                      <input
                        type="text"
                        value={platformUsersSearchTerm}
                        onChange={(event) => setPlatformUsersSearchTerm(event.target.value)}
                        placeholder="Buscar por nome, e-mail, tenant ou slug"
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                      />
                      <select
                        value={platformUsersRoleFilter}
                        onChange={(event) => setPlatformUsersRoleFilter(event.target.value)}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                      >
                        <option value="all">Todos os papéis</option>
                        {platformUserRoleOptions.map((role) => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                      <select
                        value={platformUsersStatusFilter}
                        onChange={(event) => setPlatformUsersStatusFilter(event.target.value)}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                      >
                        <option value="all">Todos os status</option>
                        {platformUsersStatusOptions.map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          setPlatformUsersSearchTerm('');
                          setPlatformUsersRoleFilter('all');
                          setPlatformUsersStatusFilter('all');
                          setPlatformUsersPage(0);
                        }}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        Limpar filtros
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto px-5 py-4">
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="pb-3 pr-4 font-semibold">Nome</th>
                          <th className="pb-3 pr-4 font-semibold">E-mail</th>
                          <th className="pb-3 pr-4 font-semibold">Papel</th>
                          <th className="pb-3 pr-4 font-semibold">Tenant</th>
                          <th className="pb-3 pr-4 font-semibold">Status</th>
                          <th className="pb-3 pr-4 font-semibold">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedPlatformUsers.map((registeredUser) => (
                          <tr key={registeredUser.id} className="border-t border-slate-100 align-top">
                            <td className="py-4 pr-4 font-semibold text-slate-800">{registeredUser.name}</td>
                            <td className="py-4 pr-4 text-slate-600">{registeredUser.email}</td>
                            <td className="py-4 pr-4 text-slate-600">{registeredUser.role}</td>
                            <td className="py-4 pr-4 text-slate-600">{registeredUser.tenantName || '-'}</td>
                            <td className="py-4 pr-4 text-slate-600">{registeredUser.active ? 'Ativo' : 'Inativo'}</td>
                            <td className="py-4 pr-4">
                              <button
                                type="button"
                                onClick={() => excluirUsuarioPlataforma(registeredUser.id)}
                                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 font-semibold text-white transition hover:bg-rose-700"
                              >
                                <Trash2 size={14} /> Excluir usuário
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredPlatformUsers.length === 0 && (
                          <tr>
                            <td className="py-5 text-center text-slate-500" colSpan={6}>Nenhum usuário encontrado com os filtros atuais</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-4">
                    <p className="text-sm text-slate-600">
                      Página {platformUsersCurrentPage + 1} de {platformUsersTotalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPlatformUsersPage((current) => Math.max(0, current - 1))}
                        disabled={platformUsersCurrentPage === 0}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        ← Anterior 10
                      </button>
                      <button
                        type="button"
                        onClick={() => setPlatformUsersPage((current) => Math.min(platformUsersTotalPages - 1, current + 1))}
                        disabled={platformUsersCurrentPage >= platformUsersTotalPages - 1}
                        className="rounded-xl bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Próximos 10 →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="rounded-[28px] bg-slate-950 p-6 text-white shadow-xl shadow-slate-300/40">
              <p className="text-sm uppercase tracking-[0.30em] text-emerald-300">Resumo</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Mês {formatMonthLongLabel(mesRelatorio)}</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMesRelatorio(getPreviousMonth(mesRelatorio))}
                    className="rounded-lg border border-white/15 px-2 py-1 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={() => setMesRelatorio(getNextMonth(mesRelatorio))}
                    className="rounded-lg border border-white/15 px-2 py-1 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                  >
                    →
                  </button>
                </div>
              </div>
              <div className="mt-4 space-y-4">
                {!isPlatformAdmin && <SummaryRow label="Contas cadastradas" value={String(resumoMensal.contas)} />}
                {!isPlatformAdmin && <SummaryRow label="Entradas cadastradas" value={String(resumoMensal.entradas)} />}
                {!isPlatformAdmin && <SummaryRow label="Investimentos" value={String(resumoMensal.investimentos)} />}
                {!isPlatformAdmin && <SummaryRow label="Extraordinárias" value={String(resumoMensal.manutencoes)} />}
                {!isPlatformAdmin && <SummaryRow label="Itens em estoque" value={String(resumoMensal.estoque)} />}
                  {isPlatformAdmin && <SummaryRow label="Tenants expirando" value={String(resumoMensal.tenantsExpiring || 0)} />}
                  {isPlatformAdmin && <SummaryRow label="Tenants inativos" value={String(resumoMensal.tenantsInactive || 0)} />}
                {isAdmin && <SummaryRow label="Usuários ativos" value={String(usuarios.filter((registeredUser) => registeredUser.active).length)} />}
                {isPlatformAdmin && <SummaryRow label="Tenants" value={String(platformTenants.length)} />}
                {isPlatformAdmin && <SummaryRow label="Usuários globais" value={String(platformUsers.length)} />}
                {isPlatformAdmin && <SummaryRow label="Super admins" value={String(platformAdmins.length)} />}
              </div>
            </div>
          </aside>
        </section>

        <footer className="relative mt-8 overflow-hidden rounded-[36px] bg-[linear-gradient(135deg,#020617_0%,#0f172a_55%,#123047_100%)] px-6 py-5 text-white shadow-[0_24px_90px_rgba(15,23,42,0.28)] md:px-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.16),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(56,189,248,0.12),transparent_26%)]" />

          <div className="relative flex flex-col gap-3 text-sm text-slate-400 md:flex-row md:items-center md:justify-between">
            <a
              href="https://smcorp.com.br"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-slate-300 transition hover:text-emerald-300"
            >
              Powered By SMCorp
            </a>
            <p>SMCustos • Plataforma de Controle Financeiro</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default FinanceApp;
